/*
 * ============================================================
 * Kinetic Carving Design Lab
 * Built by Alpas Made.
 * ============================================================
 *
 * Storefront controller for the Design Lab Board.
 *
 * Responsibilities:
 *   - Category chip row + Status chip row, ANDed with the search
 *     query, applied to the card grid
 *   - Debounced search over title, category, byline and file type
 *   - Hot / New / Top sort (Hot = votes per day, New = submitted_at
 *     desc, Top = votes desc)
 *   - Anonymous browse. Vote and submit open the sign-in dialog when
 *     signed out; both dialogs trap focus, close on Escape and on
 *     backdrop click, and restore focus to their trigger
 *   - Vote posts to the App Proxy and renders the server's count,
 *     reverting the optimistic flip if the request fails
 *   - 2-step submit dialog: details, then a review of what will be
 *     posted. Validation on blur and on submit, never on render. The
 *     details step carries the required STL / SVG file type choice
 *     (file_type=stl|svg), and the review shows it back
 *   - Detail dialog on ?design=<handle>, opened from the card's own
 *     data-* attributes and then re-checked against the read API
 *   - The discussion thread inside that dialog: read, post, reply
 *     one level, react, report, remove your own
 *   - The profile surface on its own page template: submissions,
 *     saves and comments, keyset-paginated per section
 *
 * Card metadata is read from data-* attributes on each
 * .kc-design-card. The grid is optional: on an empty board there is
 * no grid, and the dialogs still have to work, so grid-specific work
 * is guarded on `hasGrid` rather than bailing out of init().
 *
 * The vote path depends on data-design carrying a real metaobject
 * handle. That value comes from d.system.handle in Liquid, NOT
 * d.handle, which renders empty and silently posts a blank design.
 *
 * ---------------------------------------------------------------
 * Transport and endpoints
 *
 * NO ENDPOINT PATHS ARE WRITTEN DOWN IN THIS FILE. The App Proxy
 * prefix arrives on data-api-base, declared once in the
 * section, and a missing value produces a named 'not_configured'
 * failure rather than a silent post to a guessed path. That matters
 * because the proxy prefix is expected to change.
 *
 * TWO TRANSPORTS, AND THE LINE BETWEEN THEM IS IDENTITY. Anything
 * whose answer depends on WHO is asking goes through the App Proxy,
 * because only Shopify's signature over the query string can attest
 * to that: vote, submit, save, the whole comment thread including
 * its read, reactions, reports, and the profile. Only the design
 * re-check goes straight to the Vercel project over CORS, because it
 * is public and its answer is the same for everyone.
 *
 * Reading a thread cross-origin would look cheaper and would render
 * a signed-in customer the signed-out control, because `is_you` and
 * `can_comment` are questions about the reader.
 *
 * ONE NAMED STATE PER SURFACE, never a pair of booleans. The detail
 * dialog carries its whole lifecycle on data-detail-state, and the
 * two states people conflate are kept apart on purpose: `loading`
 * means there is nothing on screen yet, `stale` means the last known
 * copy is on screen and has not been confirmed.
 *
 * ---------------------------------------------------------------
 * Vote meter
 *
 * THE METER IS ONE FUNCTION, AND ITS COLOUR MEANS ONE THING. The
 * colour follows the vote count, never the moderator-set status, so
 * the bar only changes with values a visitor can move. paintMeter
 * derives the fill, the class and the label from votes against
 * threshold, so a green bar and a percentage can no longer appear
 * together, and the card and the dialog cannot drift apart.
 *
 * ---------------------------------------------------------------
 * The submit flow
 *
 * NOTHING IS VALIDATED ON RENDER. Every field carries ONE
 * named state (untouched / editing / valid / invalid), a field is only
 * ASKED on blur once it is being edited and on Continue or Submit, and
 * only an asked field can paint. Once asked it re-answers as the
 * customer types, so a corrected field clears at the keystroke that
 * fixes it. `untouched` is the state a `touched` boolean could not
 * express, and it keeps an error off a field nobody has typed in.
 *
 * ONE PRIMARY ACTION PER STEP. Step 1 offers Continue. Step 2 offers
 * Back, quieter, and Submit.
 *
 * STEP 2 IS A REVIEW. Every input lives on step 1; step 2 renders the
 * title, category, byline, brief and the reference image itself, so
 * nobody submits an idea they cannot see.
 *
 * THE WRITE CARRIES ONE NAMED STATE TOO, and an idempotency key that
 * is stable across retries. Submit is not re-enabled when the
 * response is read: re-enabling it in a .finally() would leave the
 * control live for the whole 1400ms success pause, and a second press
 * would post the design twice.
 */

(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  function debounce(fn, ms) {
    var t = null;
    return function () {
      var args = arguments;
      var ctx = this;
      window.clearTimeout(t);
      t = window.setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }

  /* Shared by both dialogs. They live outside init() so the submit
     dialog (a sibling function) can reach them for its focus trap. */
  function getFocusable(root) {
    return Array.prototype.slice.call(
      root.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])'
      )
    ).filter(function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  function trapTab(event, root) {
    var focusables = getFocusable(root);
    if (focusables.length === 0) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* One open/close implementation for all three dialogs. The sign-in
     and submit dialogs previously carried near-identical copies of
     this, and a third copy for the detail dialog would have made the
     drift permanent. Everything specific to a dialog goes through the
     onOpen / onClose / initialFocus hooks.

     Dialogs can stack: the sign-in prompt opens above the design
     dialog when a signed-out visitor presses Reply. Every open dialog
     listens on the document, so only the topmost one may act on a key,
     and the page stays locked until the last one closes. */
  var openDialogs = [];

  function createDialog(root, options) {
    var opts = options || {};
    var lastFocused = null;
    var keyHandler = null;

    function isOpen() {
      return !!root && !root.hidden;
    }

    function open() {
      if (!root || isOpen()) return;
      lastFocused = document.activeElement;
      root.hidden = false;
      openDialogs.push(root);
      document.body.classList.add('kc-modal-open');
      if (opts.onOpen) opts.onOpen();

      window.setTimeout(function () {
        var target = (opts.initialFocus && opts.initialFocus()) || getFocusable(root)[0];
        if (target && typeof target.focus === 'function') target.focus();
      }, opts.focusDelay || 30);

      keyHandler = function (event) {
        /* Only the topmost dialog handles keys. The defaultPrevented
           check stops a lower dialog from also closing when the one
           above it has just handled the same Escape and left the stack. */
        if (openDialogs[openDialogs.length - 1] !== root) return;
        if (event.key === 'Escape') {
          if (event.defaultPrevented) return;
          event.preventDefault();
          close();
          return;
        }
        if (event.key === 'Tab') trapTab(event, root);
      };
      document.addEventListener('keydown', keyHandler);
    }

    function close() {
      if (!isOpen()) return;
      root.hidden = true;
      var stackIndex = openDialogs.indexOf(root);
      if (stackIndex !== -1) openDialogs.splice(stackIndex, 1);
      if (openDialogs.length === 0) document.body.classList.remove('kc-modal-open');
      if (keyHandler) {
        document.removeEventListener('keydown', keyHandler);
        keyHandler = null;
      }
      if (opts.onClose) opts.onClose();
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    if (root && opts.closeSelector) {
      root.addEventListener('click', function (event) {
        if (!event.target.closest(opts.closeSelector)) return;
        event.preventDefault();
        close();
      });
    }

    return { open: open, close: close, isOpen: isOpen };
  }

  /* The detail dialog's lifecycle as ONE value. `loading` and `stale`
     are deliberately separate: one means nothing is on screen, the
     other means the last known copy is on screen and unconfirmed.
     Collapsing them is how a UI ends up claiming a number it has not
     checked. */
  var DETAIL = {
    LOADING: 'loading',
    EMPTY: 'empty',
    ERROR: 'error',
    STALE: 'stale',
    REVALIDATING: 'revalidating',
    FRESH: 'fresh',
  };

  /* Design handles and slugs are kebab-case, so this is nearly always
     a no-op. It is here because the value goes into an attribute
     selector, and "nearly always" is not a guarantee: a hand-edited
     handle with a quote in it would otherwise break the selector. */
  function cssEscape(value) {
    var s = String(value == null ? '' : value);
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(s);
    return s.replace(/["\\\]]/g, '\\$&');
  }

  function readParam(name) {
    try {
      return new URL(window.location.href).searchParams.get(name);
    } catch (err) {
      return null;
    }
  }

  /* ---------------------------------------------------------------
     Shared write-path plumbing for comments, reactions and reports.
     --------------------------------------------------------------- */

  /* A client-generated idempotency key, stable across retries because
     the CALLER holds it and hands the same one back. A key minted
     inside the retry would be new every time, which is the same as
     having none.

     The server pattern is /^[A-Za-z0-9_-]{8,128}$/ and a colon is
     reserved for keys the server derives itself, so nothing here may
     contain one. randomUUID is hyphenated hex and fits; the fallback
     is drawn from the same alphabet. */
  function newIdempotencyKey() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    var out = '';
    var alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
      var bytes = new Uint8Array(24);
      window.crypto.getRandomValues(bytes);
      for (var i = 0; i < bytes.length; i += 1) out += alphabet[bytes[i] % alphabet.length];
      return out;
    }
    for (var j = 0; j < 24; j += 1) {
      out += alphabet[Math.floor(Math.random() * alphabet.length)];
    }
    return out + '-' + Date.now().toString(36);
  }

  /* Storage that cannot throw. Safari in private mode throws on write,
     and a comment box that explodes because it tried to remember a
     draft is worse than one that forgets.

     EVERY KEY WRITTEN THROUGH THESE IS SCOPED TO THE VIEWER. A browser
     is shared: a laptop in a workshop, a family tablet. An unsent
     comment or a cached list left under a bare key is one account
     switch away from being shown to, and in the draft's case posted
     by, somebody else. The viewer tag comes from customer.id in
     Liquid, which is that customer's own id on their own page. */
  function makeStore(backing) {
    return {
      get: function (key) {
        try {
          var raw = window[backing].getItem(key);
          return raw ? JSON.parse(raw) : null;
        } catch (err) {
          return null;
        }
      },
      set: function (key, value) {
        try {
          window[backing].setItem(key, JSON.stringify(value));
          return true;
        } catch (err) {
          return false;
        }
      },
      remove: function (key) {
        try {
          window[backing].removeItem(key);
        } catch (err) {
          /* Nothing to recover. Every record here is advisory. */
        }
      },
    };
  }

  /* An unsent comment must survive a killed tab, which is the whole
     point of persisting it, so it goes to localStorage. */
  var store = makeStore('localStorage');

  /* The profile's stale copy must NOT outlive the tab. It is a
     convenience for a failed re-read, not an archive, and the shorter
     its life the smaller the window in which it is wrong. */
  var sessionStore = makeStore('sessionStorage');

  /* Read a JSON answer without letting a proxy error page, an empty
     body or a gateway HTML response become an unhandled exception. */
  function readJson(res) {
    return res
      .json()
      .catch(function () { return null; })
      .then(function (json) { return { res: res, json: json || {} }; });
  }

  /* One place that turns a failed response into a sentence. The server
     writes its own copy for every named refusal, so prefer that and
     only fall back where there is genuinely nothing to quote. Raw
     driver text never reaches this function: the endpoints strip it. */
  function messageFor(result, fallback) {
    var json = result && result.json ? result.json : {};
    if (typeof json.message === 'string' && json.message) return json.message;
    if (result && result.res && result.res.status === 429) {
      return 'You are doing that quickly. Give it a minute.';
    }
    return fallback;
  }

  function formatWhen(iso) {
    var t = Date.parse(iso);
    if (!isFinite(t)) return '';
    var seconds = Math.floor((Date.now() - t) / 1000);
    if (seconds < 45) return 'just now';
    if (seconds < 3600) return Math.max(1, Math.floor(seconds / 60)) + 'm ago';
    if (seconds < 86400) return Math.floor(seconds / 3600) + 'h ago';
    if (seconds < 2592000) return Math.floor(seconds / 86400) + 'd ago';
    try {
      return new Date(t).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (err) {
      return new Date(t).toDateString();
    }
  }

  /* Every element in this file is built this way. `text` goes in as
     textContent, never innerHTML, so a customer-authored string is
     inert by construction rather than by remembering to escape it.
     The comment endpoint also hands back a pre-escaped body_html; it
     is deliberately unused, because the safe path should not depend on
     the server having escaped correctly. */
  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = String(text);
    return node;
  }

  function init(board) {
    if (!board || board.dataset.kcInit === '1') return;
    board.dataset.kcInit = '1';

    var isSignedIn = board.getAttribute('data-signed-in') === 'true';
    var loginUrl = board.getAttribute('data-login-url') || '/account/login';
    var savesEnabled = board.getAttribute('data-saves-enabled') === 'true';
    var boardPath = board.getAttribute('data-board-path') || window.location.pathname;

    /* The App Proxy prefix. Declared once in the section, never here.
       An empty value is a real, named condition, not a reason to guess
       a path and post customer data at it. */
    var apiBase = (board.getAttribute('data-api-base') || '').replace(/\/+$/, '');
    function proxyUrl(path) {
      return apiBase ? apiBase + path : null;
    }

    /* HYDRATE THE VIEWER'S OWN PRESSED STATE.

       The board is server-rendered from metaobjects, and a metaobject
       does not know who is looking at it, so Liquid cannot render a
       correct aria-pressed on a vote or save button. Without this, a
       reload showed every design as unvoted even for a customer who had
       just voted, and clicking again looked like the only option.

       One request on load for the viewer's own rows. Signed-out visitors
       make no request at all. A failure leaves the buttons unpressed and
       the board fully usable, so it degrades to the previous behaviour
       rather than to a broken page. */
    function applyPressed(section, action, pressedClass) {
      if (!section || !Array.isArray(section.items)) return;
      var owned = Object.create(null);
      section.items.forEach(function (item) {
        var h = item && (item.design_handle || item.handle);
        if (h) owned[h] = true;
      });
      var nodes = document.querySelectorAll('[data-action="' + action + '"][data-design]');
      Array.prototype.forEach.call(nodes, function (btn) {
        var on = !!owned[btn.getAttribute('data-design')];
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        btn.classList.toggle(pressedClass, on);
      });
    }

    /* limit=50 because that is the endpoint's ceiling: api/design-profile.js
       answers any larger limit with a 400 (limit_invalid), which left every
       vote button unpressed after a reload. The 50 most recent votes are
       hydrated. Saves are only asked for when the board renders the saves
       control, which it does not. */
    function hydrateViewerState() {
      if (!isSignedIn) return;
      var url = proxyUrl('/design-profile?include=' + (savesEnabled ? 'votes,saves' : 'votes') + '&limit=50');
      if (!url) return;
      fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (payload) {
          if (!payload || payload.ok === false || !payload.sections) return;
          applyPressed(payload.sections.votes, 'vote', 'kc-vote--voted');
          if (savesEnabled) {
            applyPressed(payload.sections.saves, 'bookmark', 'kc-design-card__bookmark--saved');
          }
        })
        .catch(function () {
          /* Deliberately silent. The pressed state is an enhancement and
             losing it must not raise an error on a board that works. */
        });
    }

    /* Read API origin. Blank is supported: the detail dialog then
       stays on the copy already rendered into the page and says so. */
    var publicApi = (board.getAttribute('data-public-api') || '').replace(/\/+$/, '');

    var catChips = board.querySelectorAll('[data-filter-cat]');
    var statusChips = board.querySelectorAll('[data-filter-status]');
    var sortBtns = board.querySelectorAll('[data-sort-mode]');
    var searchInput = board.querySelector('[data-search]');
    var visibleCountEl = board.querySelector('[data-visible-count]');
    var noMatchesEl = board.querySelector('[data-no-matches]');
    /* An empty board renders no grid. That is not a reason to skip the rest
       of the wiring: the submit dialog and the sign-in dialog both have to
       work on the empty state, which is the launch screen and the only screen
       where submitting is the sole useful action. Everything grid-specific is
       guarded on `hasGrid` instead of bailing out here. */
    var grid = board.querySelector('.kc-design-board__grid');
    var hasGrid = !!grid;
    var cards = hasGrid
      ? Array.prototype.slice.call(grid.querySelectorAll('[data-design-card]'))
      : [];

    var state = {
      cat: '',
      status: '',
      query: '',
      sort: 'hot',
    };

    var modal = board.querySelector('[data-design-modal]');
    var modalTitle = modal ? modal.querySelector('[data-modal-title]') : null;
    var modalBody = modal ? modal.querySelector('[data-modal-body]') : null;
    var modalLogin = modal ? modal.querySelector('[data-modal-login]') : null;

    var signInDialog = createDialog(modal, {
      closeSelector: '[data-modal-close]',
      initialFocus: function () {
        return modal ? modal.querySelector('[data-modal-login]') : null;
      },
    });

    function openModal(title, body) {
      if (!modal) {
        window.location.href = loginUrl;
        return;
      }
      if (modalTitle && title) modalTitle.textContent = title;
      if (modalBody && body) modalBody.textContent = body;
      if (modalLogin) modalLogin.setAttribute('href', loginUrl);
      signInDialog.open();
    }

    /* Only actions that exist belong here, and only claims the system
       honours. The bookmark entry (its control sits behind its own
       setting) says what saving actually does: it keeps the design on
       the customer's list. It makes no notification promise, because
       nothing sends one. */
    function promptLogin(action) {
      var copy = {
        vote: {
          title: 'Sign in to vote',
          body: 'Designs in the Lab are shaped by the community. Sign in or create an account to weigh in on this one.',
        },
        submit: {
          title: 'Sign in to submit a design',
          body: 'Pitching a design takes about two minutes. Sign in or create an account to send yours to the board.',
        },
        bookmark: {
          title: 'Sign in to save designs',
          body: 'Saving keeps a design on your list so you can find it again. Sign in or create an account to start one.',
        },
        comment: {
          title: 'Sign in to join the discussion',
          body: 'Reading the thread does not need an account. Posting, replying and reacting do. Sign in or create an account to take part.',
        },
      };
      var entry = copy[action] || copy.vote;
      openModal(entry.title, entry.body);
    }

    function applyFiltersAndSort() {
      if (!hasGrid) return;
      var queryLower = state.query.trim().toLowerCase();
      var visible = 0;
      cards.forEach(function (card) {
        var cardCat = card.getAttribute('data-category') || '';
        var cardStatus = card.getAttribute('data-status') || '';
        var cardSearch = card.getAttribute('data-search') || '';
        var matchCat = !state.cat || cardCat === state.cat;
        var matchStatus = !state.status || cardStatus === state.status;
        var matchQuery = !queryLower || cardSearch.indexOf(queryLower) !== -1;
        var ok = matchCat && matchStatus && matchQuery;
        if (ok) {
          card.removeAttribute('data-hidden');
          visible += 1;
        } else {
          card.setAttribute('data-hidden', 'true');
        }
      });

      if (visibleCountEl) {
        visibleCountEl.textContent = visible + ' design' + (visible === 1 ? '' : 's');
      }
      if (noMatchesEl) {
        var hadMatches = noMatchesEl.hidden;
        noMatchesEl.hidden = visible > 0;
        /* This panel carries aria-live, but it ships `hidden` and a
           hidden region is not observed, so un-hiding it announced
           nothing: filtering a board down to zero results was silent.
           Announce on the TRANSITION only, or every keystroke that
           still matches nothing repeats the sentence. */
        if (hadMatches && visible === 0) {
          announce('No designs match those filters.');
        }
      }

      var sortedCards = cards.slice();
      sortedCards.sort(function (a, b) {
        var av = parseInt(a.getAttribute('data-votes') || '0', 10);
        var bv = parseInt(b.getAttribute('data-votes') || '0', 10);
        var ar = parseInt(a.getAttribute('data-recent') || '0', 10);
        var br = parseInt(b.getAttribute('data-recent') || '0', 10);
        if (state.sort === 'new') return br - ar;
        if (state.sort === 'top') return bv - av;
        var now = Math.floor(Date.now() / 1000);
        var aDays = Math.max(1, (now - ar) / 86400);
        var bDays = Math.max(1, (now - br) / 86400);
        return (bv / bDays) - (av / aDays);
      });
      sortedCards.forEach(function (card) {
        grid.appendChild(card);
      });
    }

    function setActiveChip(group, current) {
      group.forEach(function (chip) {
        var isActive = chip === current;
        chip.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        chip.classList.toggle('kc-chip--active', isActive);
      });
    }

    function setActiveSortBtn(current) {
      sortBtns.forEach(function (btn) {
        var isActive = btn === current;
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        btn.classList.toggle('kc-design-board__sort-btn--active', isActive);
      });
    }

    catChips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        state.cat = chip.getAttribute('data-filter-cat') || '';
        setActiveChip(catChips, chip);
        applyFiltersAndSort();
      });
    });

    statusChips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        state.status = chip.getAttribute('data-filter-status') || '';
        setActiveChip(statusChips, chip);
        applyFiltersAndSort();
      });
    });

    sortBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.sort = btn.getAttribute('data-sort-mode') || 'hot';
        setActiveSortBtn(btn);
        applyFiltersAndSort();
      });
    });

    if (searchInput) {
      var onSearch = debounce(function () {
        state.query = searchInput.value || '';
        applyFiltersAndSort();
      }, 160);
      searchInput.addEventListener('input', onSearch);
      searchInput.addEventListener('search', onSearch);
    }

    /* THE METER, AND THE ONE RULE FOR WHAT ITS COLOUR MEANS.

       Red while climbing, green once the count reaches the threshold.
       Both the fill and the label are derived from the same two
       numbers here, so they cannot disagree: there is no path through
       this function that paints a green bar next to a percentage or a
       red bar next to the word "Crossed".

       It never keys off the moderator-set status (InProduction or
       Completed): that would keep the meter red at every value a
       visitor can influence and change its colour on an event
       happening somewhere else entirely.

       One implementation, used by the card and by the detail dialog,
       because two of them drift and the drift shows up as the dialog
       and the card behind it disagreeing about the same design. */
    function paintMeter(meterEl, count, threshold) {
      var pct = threshold > 0 ? Math.min(100, Math.round((count / threshold) * 100)) : 0;
      var crossed = threshold > 0 && count >= threshold;
      if (!meterEl) return { pct: pct, crossed: crossed };

      var fill = meterEl.querySelector('[data-meter-fill]');
      if (fill) {
        fill.style.width = pct + '%';
        fill.classList.toggle('kc-meter__fill--crossed', crossed);
      }
      meterEl.setAttribute('aria-valuenow', String(count));
      if (threshold > 0) {
        meterEl.setAttribute('aria-valuemax', String(threshold));
        meterEl.setAttribute('aria-label', count + ' of ' + threshold + ' votes');
      } else {
        /* No threshold means there is no progress to describe yet.
           Announcing "0 of 0 votes" would be inventing a number. */
        meterEl.removeAttribute('aria-label');
      }
      return { pct: pct, crossed: crossed };
    }

    /* The label beside the meter. Percentage while climbing, the word
       once crossed, and the switch happens in the same call that
       repaints the bar. */
    function paintMeterLabel(pctEl, result, hasNumbers) {
      if (!pctEl) return;
      pctEl.textContent = hasNumbers
        ? (result.crossed ? 'Crossed' : result.pct + '%')
        : '';
      pctEl.classList.toggle('kc-design-card__progress-pct--crossed', hasNumbers && result.crossed);
    }

    function refreshCardMeter(card, count, threshold) {
      if (!card) return;
      card.setAttribute('data-votes', String(count));
      /* The spotlight shows the same design above the grid. Its count
         and the data-votes the dialog reads from it follow every vote,
         optimistic, reverted or confirmed, so the two never disagree. */
      var voteHandle = card.getAttribute('data-design');
      if (voteHandle) {
        var spot = board.querySelector('[data-design-detail][data-design="' + cssEscape(voteHandle) + '"]');
        if (spot) {
          spot.setAttribute('data-votes', String(count));
          var spotVotes = spot.querySelector('[data-featured-votes]');
          if (spotVotes) spotVotes.textContent = String(count);
        }
      }
      var threshNum = parseInt(threshold, 10) || parseInt(card.getAttribute('data-threshold') || '0', 10);
      var countEl = card.querySelector('[data-progress-count]');
      if (countEl) countEl.textContent = count + ' of ' + threshNum + ' votes';
      var result = paintMeter(card.querySelector('[data-meter]'), count, threshNum);
      paintMeterLabel(card.querySelector('[data-progress-pct]'), result, threshNum > 0);
    }

    /* Vote toggle via the Shopify App Proxy. Optimistic UI flips the
       button and the count immediately, then snaps to the authoritative
       response (or reverts on error). The per-button `_kcVoteInflight`
       guard keeps a double-click from racing two writes against the
       same record. */
    async function castVote(voteBtn) {
      if (voteBtn._kcVoteInflight) return;
      var card = voteBtn.closest('[data-design-card]');
      /* A design whose author pulled it takes no new votes. The server
         refuses this with a 409 in any case; refusing here as well means
         the customer is told why instead of watching a button flip and
         flip back. The votes it already had are untouched, which is why
         the sentence says so. */
      if (card && card.getAttribute('data-withdrawn') === 'true') {
        showToast('That design was withdrawn by its author. The votes it already had still stand.');
        return;
      }
      var handle = voteBtn.getAttribute('data-design');
      var countEl = voteBtn.querySelector('[data-vote-count]');
      var threshold = card ? parseInt(card.getAttribute('data-threshold') || '0', 10) : 0;
      var beforeCount = parseInt((countEl && countEl.textContent) || '0', 10);
      var beforeVoted = voteBtn.getAttribute('aria-pressed') === 'true';

      /* Optimistic: flip the state and count immediately. */
      voteBtn._kcVoteInflight = true;
      voteBtn.classList.add('is-loading');
      voteBtn.setAttribute('aria-pressed', beforeVoted ? 'false' : 'true');
      voteBtn.classList.toggle('kc-vote--voted', !beforeVoted);
      var optimisticCount = Math.max(0, beforeCount + (beforeVoted ? -1 : 1));
      if (countEl) countEl.textContent = String(optimisticCount);
      refreshCardMeter(card, optimisticCount, threshold);

      function revert() {
        voteBtn.setAttribute('aria-pressed', beforeVoted ? 'true' : 'false');
        voteBtn.classList.toggle('kc-vote--voted', beforeVoted);
        if (countEl) countEl.textContent = String(beforeCount);
        refreshCardMeter(card, beforeCount, threshold);
      }

      var endpoint = proxyUrl('/design-vote?design=' + encodeURIComponent(handle));
      if (!endpoint) {
        /* No proxy prefix configured. Say so as a named condition
           instead of posting to a guessed path. */
        revert();
        voteBtn._kcVoteInflight = false;
        voteBtn.classList.remove('is-loading');
        showToast('Voting is not configured on this page yet.');
        return;
      }

      try {
        /* The direction is said out loud. The server reads a POST with
           no intent as "ensure voted" and never as a toggle, so without
           it removing a vote is impossible. A fresh key per click: the
           key the server derives when none is sent ignores intent, and
           an "off" soon after the "on" would be refused as a reuse. */
        var res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            intent: beforeVoted ? 'off' : 'on',
            idempotencyKey: newIdempotencyKey(),
          }),
        });
        var payload = await res.json().catch(function () { return {}; });
        if (!res.ok || !payload.ok) {
          revert();
          if (res.status === 401) {
            promptLogin('vote');
          } else {
            showToast(payload.message || 'Could not save your vote. Please try again.');
          }
          return;
        }
        /* Snap to authoritative state. */
        voteBtn.setAttribute('aria-pressed', payload.voted ? 'true' : 'false');
        voteBtn.classList.toggle('kc-vote--voted', !!payload.voted);
        if (countEl) countEl.textContent = String(payload.count);
        refreshCardMeter(card, payload.count, payload.threshold);

        if (payload.justCrossed) {
          showToast('Threshold reached. The team will see this design.');
        }
      } catch (err) {
        console.error('[design-vote] failed', err);
        revert();
        showToast('Network error. Please try again.');
      } finally {
        voteBtn._kcVoteInflight = false;
        voteBtn.classList.remove('is-loading');
      }
    }

    /* ---------------------------------------------------------------
       Detail dialog.

       Opens from the clicked card's own data-* attributes, so there is
       never a spinner over information the page already rendered. Then
       it re-checks the parts that move against the read API. The URL
       changes to ?design=<handle> so the view is shareable and the back
       button closes it, and the filters stay exactly where they were,
       which a page navigation would have thrown away.
       --------------------------------------------------------------- */
    var detailRoot = board.querySelector('[data-detail-modal]');
    var detail = null;

    if (detailRoot) {
      var d = {
        image: detailRoot.querySelector('[data-detail-image]'),
        placeholder: detailRoot.querySelector('[data-detail-placeholder]'),
        status: detailRoot.querySelector('[data-detail-status]'),
        statusLabel: detailRoot.querySelector('[data-detail-status-label]'),
        category: detailRoot.querySelector('[data-detail-category]'),
        title: detailRoot.querySelector('[data-detail-title]'),
        byline: detailRoot.querySelector('[data-detail-byline]'),
        tomb: detailRoot.querySelector('[data-detail-tomb]'),
        tombCopy: detailRoot.querySelector('[data-detail-tomb-copy]'),
        description: detailRoot.querySelector('[data-detail-description]'),
        votesLabel: detailRoot.querySelector('[data-detail-votes-label]'),
        pct: detailRoot.querySelector('[data-detail-pct]'),
        meter: detailRoot.querySelector('[data-detail-meter]'),
        note: detailRoot.querySelector('[data-detail-note]'),
        product: detailRoot.querySelector('[data-detail-product]'),
        productLabel: detailRoot.querySelector('[data-detail-product-label]'),
        feedback: detailRoot.querySelector('[data-detail-feedback]'),
        freshness: detailRoot.querySelector('[data-detail-freshness]'),
        retry: detailRoot.querySelector('[data-detail-retry]'),
        filetype: detailRoot.querySelector('[data-detail-filetype]'),
        vote: detailRoot.querySelector('[data-detail-vote]'),
        voteCount: detailRoot.querySelector('[data-detail-vote-count]'),
      };

      /* THE DIALOG'S VOTE.

         It is the grid card's vote, mirrored. Pressing it presses the
         card's own button through castVote(), so the optimistic flip,
         the rollback, the sign-in prompt, the withdrawn refusal and the
         in-flight guard are one code path, and the dialog can never show
         a different state from the card behind it. An observer on the
         grid repaints the dialog whenever that card changes, which also
         covers the viewer's own vote arriving after a reload. A design
         with no live card (withdrawn, or a link to something not on the
         board) shows no vote button rather than one that cannot work. */
      var detailVoteHandle = '';

      function gridVoteFor(handle) {
        if (!handle || !grid) return null;
        var card = grid.querySelector('[data-design-card][data-design="' + cssEscape(handle) + '"]');
        if (!card || card.getAttribute('data-withdrawn') === 'true') return null;
        return card.querySelector('[data-action="vote"]');
      }

      function syncDetailVote() {
        if (!d.vote) return;
        var src = gridVoteFor(detailVoteHandle);
        if (!src) {
          d.vote.hidden = true;
          return;
        }
        d.vote.hidden = false;
        var pressed = src.getAttribute('aria-pressed') === 'true';
        d.vote.setAttribute('aria-pressed', pressed ? 'true' : 'false');
        d.vote.classList.toggle('kc-vote--voted', pressed);
        d.vote.classList.toggle('is-loading', src.classList.contains('is-loading'));
        var label = src.getAttribute('aria-label');
        if (label) d.vote.setAttribute('aria-label', label);

        var srcCount = src.querySelector('[data-vote-count]');
        var n = srcCount ? (parseInt(srcCount.textContent || '0', 10) || 0) : 0;
        if (d.voteCount) d.voteCount.textContent = String(n);

        var card = src.closest('[data-design-card]');
        var threshold = card ? (parseInt(card.getAttribute('data-threshold') || '0', 10) || 0) : 0;
        if (threshold > 0) {
          if (d.votesLabel) d.votesLabel.textContent = n + ' of ' + threshold + ' votes';
          paintMeterLabel(d.pct, paintMeter(d.meter, n, threshold), true);
          if (d.note) d.note.textContent = thresholdNote(card.getAttribute('data-status') || '', n, threshold);
        }
      }

      if (d.vote) {
        d.vote.addEventListener('click', function (event) {
          event.preventDefault();
          var src = gridVoteFor(detailVoteHandle);
          if (!src) return;
          /* Same order as the grid: the sign-in prompt first, so a
             signed-out visitor is never shown a vote that snaps back. */
          if (!isSignedIn) {
            promptLogin('vote');
            return;
          }
          castVote(src);
          syncDetailVote();
        });
      }

      if (grid && 'MutationObserver' in window) {
        new MutationObserver(function () {
          if (detailVoteHandle && detail && detail.isOpen()) syncDetailVote();
        }).observe(grid, {
          subtree: true,
          childList: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['aria-pressed', 'class', 'data-votes', 'data-withdrawn'],
        });
      }

      var comments = initComments(detailRoot, {
        proxyUrl: proxyUrl,
        isSignedIn: isSignedIn,
        promptLogin: promptLogin,
        enabled: board.getAttribute('data-comments-enabled') === 'true',
        /* 'anon' rather than an empty string, so a signed-out draft has
           its own namespace and can never be handed to the first
           account that signs in on this browser. */
        viewerTag: board.getAttribute('data-viewer-tag') || 'anon',
      });

      /* Latest-wins. Two re-checks can be in flight after a fast
         open-close-open, and the slower one must not overwrite the
         newer answer. */
      var detailToken = 0;
      var detailKey = null;
      var hasLocalCopy = false;
      var pushedHistory = false;

      var FRESHNESS = {};
      FRESHNESS[DETAIL.LOADING] = 'Loading this design...';
      FRESHNESS[DETAIL.EMPTY] = '';
      FRESHNESS[DETAIL.ERROR] = 'Could not load this design.';
      FRESHNESS[DETAIL.STALE] = 'Showing the counts from this page.';
      FRESHNESS[DETAIL.REVALIDATING] = 'Re-checking the counts...';
      FRESHNESS[DETAIL.FRESH] = 'Counts confirmed just now.';

      /* The three states with no design to name still need an
         accessible name: the dialog is labelled by this heading, and
         an empty one leaves a dialog announced as nothing. */
      var HEADINGS = {};
      HEADINGS[DETAIL.LOADING] = 'Loading';
      HEADINGS[DETAIL.EMPTY] = 'Not on the board';
      HEADINGS[DETAIL.ERROR] = 'Could not load';

      function setDetailState(next, message) {
        detailRoot.setAttribute('data-detail-state', next);
        if (d.freshness) d.freshness.textContent = FRESHNESS[next] || '';
        if (d.feedback) d.feedback.textContent = message || '';
        if (d.title && HEADINGS[next] && !hasLocalCopy) d.title.textContent = HEADINGS[next];
        if (d.retry) {
          /* Retry is offered only where retrying can change the
             answer. With no read API configured there is nothing to
             re-check, so offering the button would be theatre. */
          d.retry.hidden = !publicApi || (next !== DETAIL.STALE && next !== DETAIL.ERROR);
        }
      }

      /* Threshold copy, said the way the system actually behaves.
         Crossing the threshold is a review signal; it is not a
         production decision and never flips the status on its own. */
      function thresholdNote(status, count, threshold) {
        if (status === 'Completed') return 'Forged and on sale.';
        if (status === 'InProduction') return 'David is cutting this one now.';
        if (threshold > 0 && count >= threshold) {
          return 'Past ' + threshold + ' votes. It is queued for David to review; reaching the threshold flags a design, it does not schedule production.';
        }
        var left = Math.max(0, threshold - count);
        return left + ' more ' + (left === 1 ? 'vote' : 'votes') + ' puts this in front of David for review. He decides what gets cut.';
      }

      /* THE TOMBSTONE.

         The read API has already replaced the title, the brief and the
         image with its own removal notice before this dialog ever sees
         them, so nothing here re-words the author's content. This paints
         the one sentence that says why the words changed, and it is set
         and CLEARED on every render, so a tombstone from the design
         looked at a moment ago cannot survive onto the next one.

         `model.withdrawn` comes from the server's named state and from
         nothing else. Neither a missing title nor a null image is taken
         as evidence: both have ordinary causes. */
      function paintTombstone(model) {
        var pulled = !!model.withdrawn;
        detailRoot.setAttribute('data-detail-withdrawn', pulled ? 'true' : 'false');
        if (d.tomb) d.tomb.hidden = !pulled;
        if (!d.tombCopy) return;
        if (!pulled) {
          d.tombCopy.textContent = '';
          return;
        }
        var when = model.withdrawnAt ? formatWhen(model.withdrawnAt) : '';
        d.tombCopy.textContent =
          'The maker who submitted this design removed it' + (when ? ' ' + when : '') +
          '. The votes and comments people left are still here.';
      }

      function render(model) {
        if (d.title) d.title.textContent = model.title || '';
        if (d.category) d.category.textContent = model.category || '';
        if (d.byline) d.byline.textContent = model.byline ? '@' + model.byline : '';
        if (d.description) d.description.textContent = model.description || '';
        paintTombstone(model);

        if (d.image) {
          if (model.image) {
            d.image.src = model.image;
            d.image.alt = model.title || '';
            d.image.hidden = false;
          } else {
            d.image.removeAttribute('src');
            d.image.hidden = true;
          }
        }
        if (d.placeholder) d.placeholder.hidden = !!model.image;

        if (d.status) {
          var label = model.statusLabel || model.status || '';
          d.status.hidden = !label;
          d.status.className = 'kc-design-card__status kc-detail-modal__status' +
            (model.status ? ' kc-design-card__status--' + String(model.status).toLowerCase() : '');
          if (d.statusLabel) d.statusLabel.textContent = label;
        }

        var count = model.votes || 0;
        var threshold = model.threshold || 0;
        /* A threshold of zero means this is the skeleton, not a design
           with no threshold. Writing "0 / 0 votes" and "0 more votes
           puts this in front of David" over a loading dialog would be
           inventing numbers, so the whole block stays blank until
           there is something real to say. */
        var hasNumbers = threshold > 0;
        if (d.votesLabel) d.votesLabel.textContent = hasNumbers ? count + ' of ' + threshold + ' votes' : '';
        /* Same meter, same rule, same words as the card behind this
           dialog. The label never reflects the moderator-set status,
           because that answers a different question from the one the
           bar is drawing. */
        paintMeterLabel(d.pct, paintMeter(d.meter, count, threshold), hasNumbers);
        if (d.note) d.note.textContent = hasNumbers ? thresholdNote(model.status, count, threshold) : '';

        /* The file type the submitter asked for, the same badge the card
           shows. Anything that is not STL or SVG shows nothing rather
           than a guess. */
        if (d.filetype) {
          var ft = model.fileType === 'STL' || model.fileType === 'SVG' ? model.fileType : '';
          d.filetype.hidden = !ft;
          d.filetype.textContent = ft;
          d.filetype.className = 'kc-detail-modal__filetype' + (ft ? ' kc-detail-modal__filetype--' + ft.toLowerCase() : '');
          if (ft) d.filetype.setAttribute('aria-label', 'File type ' + ft);
        }
        detailVoteHandle = model.handle || '';
        syncDetailVote();

        if (d.product) {
          if (model.productUrl) {
            d.product.href = model.productUrl;
            d.product.hidden = false;
            if (d.productLabel) {
              d.productLabel.textContent = model.productTitle
                ? 'Buy ' + model.productTitle
                : 'Buy this piece';
            }
          } else {
            d.product.hidden = true;
          }
        }

      }

      /* Every field the dialog shows, read off the element the visitor
         clicked. This is the "last known copy": correct as of the page
         render, and labelled as such until the API confirms it. */
      function modelFromElement(el) {
        var num = function (name) { return parseInt(el.getAttribute(name) || '0', 10) || 0; };
        return {
          handle: el.getAttribute('data-design') || '',
          slug: el.getAttribute('data-slug') || '',
          title: el.getAttribute('data-title') || '',
          byline: el.getAttribute('data-byline') || '',
          description: el.getAttribute('data-description') || '',
          image: el.getAttribute('data-image') || '',
          category: el.getAttribute('data-category') || '',
          status: el.getAttribute('data-status') || '',
          statusLabel: el.getAttribute('data-status-label') || '',
          votes: num('data-votes'),
          threshold: num('data-threshold'),
          comments: num('data-comments'),
          url: el.getAttribute('data-url') || '',
          productUrl: el.getAttribute('data-product-url') || '',
          productTitle: el.getAttribute('data-product-title') || '',
          fileType: (el.getAttribute('data-file-type') || '').toUpperCase(),
        };
      }

      function modelFromPayload(payload) {
        var design = payload.design || {};
        var votes = payload.votes || {};
        /* The withdrawal block, taken as the server states it. Three
           states, not a boolean: `withdrawn`, `live`, and `unknown` for
           the case where the check itself could not run. Only the first
           of those closes anything down. */
        var pulled = payload.withdrawn || {};
        var status = design.status || '';
        var statusLabel = status;
        if (status === 'InProduction') statusLabel = 'In Production';
        if (status === 'Completed') statusLabel = 'Forged';
        return {
          handle: design.handle || '',
          slug: design.slug || '',
          title: design.title || '',
          byline: design.byline || '',
          description: design.description || '',
          image: design.image || '',
          category: design.category || '',
          status: status,
          statusLabel: statusLabel,
          votes: typeof votes.count === 'number' ? votes.count : (design.vote_count || 0),
          threshold: votes.threshold || design.threshold || 0,
          comments: (payload.comments && payload.comments.cached_count) || design.comment_count || 0,
          url: boardPath + '?design=' + encodeURIComponent(design.handle || design.slug || ''),
          productUrl: (design.product && design.product.url) || design.product_url || '',
          productTitle: '',
          /* The server has its own named answer for whether it could
             confirm the count. Trusting it is how a confirmed number
             and an unconfirmable one stay distinguishable end to end. */
          confirmed: votes.state === 'confirmed',
          withdrawn: pulled.state === 'withdrawn',
          withdrawnAt: pulled.at || null,
          /* Said by the server rather than derived from `withdrawn`
             here, so the two can never disagree. Absent means unknown,
             and unknown leaves the composer open: a refusal from the
             endpoint keeps the customer's words on screen, a wrongly
             hidden box does not. */
          acceptsWrites: pulled.accepts_writes !== false,
        };
      }

      /* Keep the board and the dialog telling the same story. A count
         confirmed in the dialog must not leave a different number on
         the card behind it. */
      function syncCard(model) {
        if (!model.handle) return;
        var card = board.querySelector('[data-design-card][data-design="' + cssEscape(model.handle) + '"]');
        if (!card) return;
        var countEl = card.querySelector('[data-vote-count]');
        if (countEl) countEl.textContent = String(model.votes);
        refreshCardMeter(card, model.votes, model.threshold);
        if (model.withdrawn) markCardWithdrawn(card, model);
      }

      /* THE CARD BEHIND THE DIALOG.

         The grid's first paint is server-rendered Liquid over the
         metaobjects, which know nothing about a withdrawal, so a card
         can be on screen carrying a title its author has since pulled.
         The moment the read API tells us that has happened, this takes
         the author's words off that card and takes the two write
         controls with them.

         SCOPE, SAID OUT LOUD: this only ever reaches a card whose
         dialog somebody opened, because that re-check is the only place
         the truth arrives. The complete fix is the Liquid feed itself
         filtering withdrawn handles, which is a decision above this
         file. What this does guarantee is that we never leave a live
         Vote button on a design we have just been told is withdrawn.

         The data-* attributes are rewritten too, not just the visible
         text: modelFromElement() reads them back on the next open, and
         leaving them would flash the pulled title up again. */
      function markCardWithdrawn(card, model) {
        if (card.getAttribute('data-withdrawn') === 'true') return;
        card.setAttribute('data-withdrawn', 'true');

        var notice = model.title || 'Withdrawn by the author';
        card.setAttribute('data-title', notice);
        card.setAttribute('data-description', model.description || '');
        card.setAttribute('data-image', '');
        card.setAttribute('data-search', '');

        var title = card.querySelector('.kc-design-card__title');
        if (title) title.textContent = notice;
        var opener = card.querySelector('[data-design-open]');
        if (opener) opener.setAttribute('aria-label', 'View ' + notice);
        /* Swapped for the placeholder the card already uses for a design
           with no reference image, not simply removed: an empty image
           well is a broken card, and the placeholder is the shape this
           component already has for "there is no picture here". */
        var image = card.querySelector('img.kc-design-card__image');
        if (image && image.parentNode) {
          var blank = el('div', 'kc-design-card__image kc-design-card__image--placeholder');
          blank.setAttribute('aria-hidden', 'true');
          image.parentNode.replaceChild(blank, image);
        }

        ['[data-action="vote"]', '[data-action="bookmark"]'].forEach(function (sel) {
          var btn = card.querySelector(sel);
          if (!btn) return;
          btn.disabled = true;
          btn.setAttribute('aria-disabled', 'true');
          btn.setAttribute('aria-label', 'Withdrawn by its author. This design no longer takes votes.');
        });
      }

      /* Handle first, slug only as a fallback. The handle is unique;
         the slug is the bare slugified title, so two designs with the
         same title share one. A single combined selector returns the
         first match in document order, which could be the other
         design's slug before this design's handle. */
      function findSource(key) {
        if (!key) return null;
        var safe = cssEscape(key);
        return board.querySelector(
          '[data-design-card][data-design="' + safe + '"],' +
          '[data-design-detail][data-design="' + safe + '"]'
        ) || board.querySelector(
          '[data-design-card][data-slug="' + safe + '"],' +
          '[data-design-detail][data-slug="' + safe + '"]'
        );
      }

      function revalidate(key) {
        if (!publicApi) {
          /* Nothing to re-check against. This is the honest resting
             state, not an error: the page's own copy is what we have.
             With no card on the page there is no copy at all, and a
             link with nothing to load it from must not sit on
             "Loading" forever, so it gets the empty state instead. */
          if (hasLocalCopy) {
            setDetailState(DETAIL.STALE);
          } else {
            setDetailState(DETAIL.EMPTY, 'That design is not on the board.');
          }
          return;
        }

        var token = ++detailToken;
        setDetailState(hasLocalCopy ? DETAIL.REVALIDATING : DETAIL.LOADING);

        fetch(publicApi + '/api/design-detail?design=' + encodeURIComponent(key) + '&include=votes,comments', {
          headers: { Accept: 'application/json' },
        })
          .then(function (res) {
            return res.json()
              .catch(function () { return null; })
              .then(function (json) { return { res: res, json: json }; });
          })
          .then(function (result) {
            if (token !== detailToken) return;
            var payload = result.json;

            if (result.res.status === 404) {
              if (hasLocalCopy) {
                setDetailState(DETAIL.STALE, 'This design is no longer on the board.');
              } else {
                setDetailState(DETAIL.EMPTY, 'That design is not on the board.');
              }
              return;
            }
            if (!result.res.ok || !payload || !payload.ok) {
              setDetailState(
                hasLocalCopy ? DETAIL.STALE : DETAIL.ERROR,
                'The latest counts could not be loaded.'
              );
              return;
            }

            var model = modelFromPayload(payload);
            /* The API returns the product handle, not the product's
               title; Liquid already resolved that when it rendered the
               card. Keep it rather than replacing a real product name
               with a generic label on every successful re-check. */
            var localSource = findSource(key);
            if (localSource && !model.productTitle) {
              model.productTitle = localSource.getAttribute('data-product-title') || '';
            }
            render(model);
            hasLocalCopy = true;
            syncCard(model);
            setDetailState(model.confirmed ? DETAIL.FRESH : DETAIL.STALE);

            /* The thread is addressed by the METAOBJECT HANDLE, never
               the slug: the comment endpoint resolves a design with
               `where handle = $1`. A deep link opened with no card on
               the page has only the URL key, so this is the first
               moment the real handle is known, and openThread is a
               no-op when it is already the one on screen. */
            comments.openThread(model.handle);
            /* AFTER openThread, deliberately. openThread resets the
               thread's own state when the handle changes, so telling it
               the design is closed first would be overwritten by the
               reset a line later. */
            comments.setWritable(model.acceptsWrites);
          })
          .catch(function (err) {
            if (token !== detailToken) return;
            console.error('[design-detail] re-check failed', err);
            setDetailState(
              hasLocalCopy ? DETAIL.STALE : DETAIL.ERROR,
              'The latest counts could not be loaded.'
            );
          });
      }

      detail = createDialog(detailRoot, {
        closeSelector: '[data-detail-close]',
        onClose: function () {
          /* Abandon any in-flight re-check so a late response cannot
             repaint a dialog nobody is looking at. */
          detailToken += 1;
          detailKey = null;
          hasLocalCopy = false;
          comments.reset();
          if (pushedHistory) {
            pushedHistory = false;
            /* Back, so the entry we added is removed rather than a
               second one being stacked on top of it. */
            window.history.back();
          } else {
            window.history.replaceState({}, '', boardPath);
          }
        },
      });

      detail.openDesign = function (key, options) {
        var opts = options || {};
        if (!key) return;
        var wasOpen = detail.isOpen();

        var source = findSource(key);
        hasLocalCopy = !!source;

        if (source) {
          var model = modelFromElement(source);
          render(model);
          setDetailState(DETAIL.STALE);
          /* The card already carries the handle, so the thread starts
             loading in the same frame the dialog opens rather than
             waiting on the design re-check. */
          comments.openThread(model.handle);
          /* Addressed by HANDLE from here on, whatever key found it,
             so the URL, the re-check and the Re-check button all use
             the one key that is unique. An old slug link still
             resolves: findSource and design-detail both fall back to
             the slug. */
          key = model.handle || model.slug || key;
        } else {
          /* Deep link to something not on this page. Nothing to show
             yet, so this is genuinely loading, not stale. */
          render({ title: '', category: '', byline: '', description: '', image: '', status: '', votes: 0, threshold: 0, comments: 0 });
          setDetailState(DETAIL.LOADING);
        }

        detailKey = key;

        if (opts.push !== false) {
          var url = boardPath + '?design=' + encodeURIComponent(key);
          if (wasOpen) {
            /* Swapping designs inside an open dialog replaces the
               entry. Pushing would make one Back press walk backwards
               through every design the visitor looked at. */
            window.history.replaceState({ kcDesign: key }, '', url);
          } else {
            window.history.pushState({ kcDesign: key }, '', url);
            pushedHistory = true;
          }
        }

        detail.open();
        revalidate(key);
      };

      if (d.retry) {
        d.retry.addEventListener('click', function () {
          if (detailKey) revalidate(detailKey);
        });
      }

      /* One delegated listener on the whole board, so the spotlight
         card outside the grid is covered by the same code path. */
      board.addEventListener('click', function (event) {
        var opener = event.target.closest('[data-design-open]');
        if (!opener || !board.contains(opener)) return;
        /* Leave modified clicks alone: the href is real and a visitor
           asking for a new tab should get one. */
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
        event.preventDefault();
        var host = opener.closest('[data-design-card], [data-design-detail]');
        var key = host
          ? (host.getAttribute('data-design') || host.getAttribute('data-slug'))
          : null;
        if (key) detail.openDesign(key);
      });

      window.addEventListener('popstate', function () {
        var key = readParam('design');
        if (key) {
          detail.openDesign(key, { push: false });
        } else {
          pushedHistory = false;
          detail.close();
        }
      });

      /* Deep link on first paint. The dead ?id=<handle> link this
         replaces is the reason cards taught visitors they were not
         clickable. */
      var initialKey = readParam('design');
      if (initialKey) detail.openDesign(initialKey, { push: false });
    }

    if (hasGrid) {
      grid.addEventListener('click', function (event) {
        var voteBtn = event.target.closest('[data-action="vote"]');
        if (voteBtn) {
          event.preventDefault();
          if (!isSignedIn) {
            promptLogin('vote');
            return;
          }
          castVote(voteBtn);
          return;
        }

        var bookmarkBtn = event.target.closest('[data-action="bookmark"]');
        if (bookmarkBtn) {
          event.preventDefault();
          toggleBookmark(bookmarkBtn);
        }
      });
    }

    /* Saves. The control is not rendered (data-saves-enabled is
       hard-coded false in the section); this path is kept for when the
       saves endpoint ships, and is written to be correct rather than to
       be exercised today.
       It states its own failure instead of leaving a pressed button
       that saved nothing. */
    async function toggleBookmark(btn) {
      if (!savesEnabled || btn._kcSaveInflight) return;
      if (!isSignedIn) {
        promptLogin('bookmark');
        return;
      }
      var host = btn.closest('[data-design-card]');
      if (host && host.getAttribute('data-withdrawn') === 'true') {
        showToast('That design was withdrawn by its author and can no longer be saved.');
        return;
      }

      var handle = btn.getAttribute('data-design');
      var endpoint = proxyUrl('/design-save?design=' + encodeURIComponent(handle));
      if (!endpoint) {
        showToast('Saving is not configured on this page yet.');
        return;
      }

      var wasSaved = btn.getAttribute('aria-pressed') === 'true';
      btn._kcSaveInflight = true;
      btn.classList.add('is-loading');

      try {
        var res = await fetch(endpoint, {
          method: wasSaved ? 'DELETE' : 'POST',
          headers: { Accept: 'application/json' },
        });
        var payload = await res.json().catch(function () { return {}; });
        if (!res.ok || !payload.ok) {
          if (res.status === 401) {
            promptLogin('bookmark');
          } else {
            showToast('Could not update your saved designs. Please try again.');
          }
          return;
        }
        /* Pressed state comes from the server's answer, never from the
           click. A button that shows "saved" because it was clicked is
           the same defect as a progress bar that shows "done" because
           the bytes left the browser. */
        var saved = !!payload.saved;
        btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
        btn.classList.toggle('kc-design-card__bookmark--saved', saved);
      } catch (err) {
        console.error('[design-save] failed', err);
        showToast('Network error. Please try again.');
      } finally {
        btn._kcSaveInflight = false;
        btn.classList.remove('is-loading');
      }
    }

    /* Empty-result recovery. The no-matches message carries a control
       that clears the search and filters. */
    var clearFiltersBtn = board.querySelector('[data-clear-filters]');
    if (clearFiltersBtn) {
      clearFiltersBtn.addEventListener('click', function () {
        state.cat = '';
        state.status = '';
        state.query = '';
        if (searchInput) searchInput.value = '';
        var firstCat = board.querySelector('[data-filter-cat=""]');
        if (firstCat) setActiveChip(catChips, firstCat);
        var firstStatus = board.querySelector('[data-filter-status=""]');
        if (firstStatus) setActiveChip(statusChips, firstStatus);
        applyFiltersAndSort();
        if (searchInput && typeof searchInput.focus === 'function') searchInput.focus();
      });
    }

    var submitModal = board.querySelector('[data-submit-modal]');
    var submitFormCtl = submitModal ? initSubmitForm(submitModal, board) : null;

    /* Every opener, not just the first: an empty board renders a second
       Submit a Design button in the empty state. */
    board.querySelectorAll('[data-action="open-submit"]').forEach(function (submitBtn) {
      submitBtn.addEventListener('click', function (event) {
        event.preventDefault();
        if (!isSignedIn) {
          promptLogin('submit');
          return;
        }
        if (submitFormCtl) {
          submitFormCtl.open();
        } else {
          showToast('Submit form is unavailable right now.');
        }
      });
    });

    /* A link from elsewhere on the store (the Design Lab invite under the buy
       area on product pages) asks for the submit form with ?submit=1. It goes
       through the same button, so a signed-out visitor gets the same sign-in
       prompt. The parameter is removed first, so a reload or Back does not
       open the form again. */
    if (readParam('submit') === '1') {
      try {
        var cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('submit');
        window.history.replaceState(window.history.state, '', cleanUrl.pathname + cleanUrl.search + cleanUrl.hash);
      } catch (err) { /* the form still opens */ }
      var firstOpener = board.querySelector('[data-action="open-submit"]');
      if (firstOpener) firstOpener.click();
    }

    applyFiltersAndSort();

    /* Last, so the board is interactive before the network is touched.
       Pressed state is an enhancement layered onto a working board, not
       a precondition for one. */
    hydrateViewerState();
  }

  function initSubmitForm(modal, board) {
    var form = modal.querySelector('[data-submit-form]');
    if (!form) return null;

    /* Same single source as the vote path. The form's own `action`
       attribute is rendered from the identical Liquid value, so the
       no-JS post and the fetch below cannot drift apart. */
    var apiBase = (board.getAttribute('data-api-base') || '').replace(/\/+$/, '');

    var stepEls = form.querySelectorAll('[data-step]');
    var stepMarkers = form.querySelectorAll('[data-step-marker]');
    var reviewStep = form.querySelector('[data-step="2"]');
    var stepLabel = form.querySelector('[data-step-label]');
    var nextBtn = form.querySelector('[data-step-next]');
    var backBtn = form.querySelector('[data-step-back]');
    var submitFormBtn = form.querySelector('[data-step-submit]');
    var submitLabel = submitFormBtn ? submitFormBtn.querySelector('[data-btn-label]') : null;
    var statusEl = form.querySelector('[data-submit-status]');

    var titleInput = form.querySelector('[name="title"]');
    var descInput = form.querySelector('[name="description"]');
    /* The byline is no longer an input. The server takes the name from
       the Shopify customer record, so the review step reads the value the
       page was rendered with rather than something the customer typed. */
    var bylineValue = form.querySelector('[data-byline-value]');
    var descCounter = form.querySelector('[data-description-counter]');
    var categoryGroup = form.querySelector('[data-category-group]');
    var categoryHidden = form.querySelector('[data-category-value]');
    /* File type: native radios named file_type (stl | svg) inside chips.
       The browser owns the checked state and keyboard behaviour; this code
       only mirrors the state onto the chip and reports a missing choice. */
    var fileTypeGroup = form.querySelector('[data-filetype-group]');
    var fileTypeInputs = fileTypeGroup
      ? Array.prototype.slice.call(fileTypeGroup.querySelectorAll('input[name="file_type"]'))
      : [];
    var fileInput = form.querySelector('[data-dropzone-input]');
    var dropzone = form.querySelector('[data-dropzone]');
    var dropzoneInner = form.querySelector('[data-dropzone-inner]');
    var dropzonePreview = form.querySelector('[data-dropzone-preview]');
    var dropzoneImg = form.querySelector('[data-dropzone-img]');
    var dropzoneName = form.querySelector('[data-dropzone-name]');
    var dropzoneSize = form.querySelector('[data-dropzone-size]');
    var dropzoneRemove = form.querySelector('[data-dropzone-remove]');

    var reviewImage = form.querySelector('[data-review-image]');
    var reviewNoImage = form.querySelector('[data-review-noimage]');
    var reviewCategory = form.querySelector('[data-review-category]');
    var reviewTitle = form.querySelector('[data-review-title]');
    var reviewByline = form.querySelector('[data-review-byline]');
    var reviewFileType = form.querySelector('[data-review-filetype]');
    var reviewDescription = form.querySelector('[data-review-description]');

    /* The step lives on the [data-step-marker] elements and on which
       buttons are hidden, which is one place. A `currentStep` mirror
       was written on every step change and read by nothing; a second
       copy of a fact is a drift waiting to happen. */
    var debounceTimers = {};
    var maxFileSize = 4 * 1024 * 1024;
    var allowedMime = ['image/jpeg', 'image/png', 'image/webp'];

    /* Every field the customer fills is on step 1 now. Step 2 shows
       them back and takes no input, so there is exactly one list to
       validate and one place to send them when something is wrong. */
    var FIELDS = ['title', 'category', 'file_type', 'image', 'description'];

    /* ONE NAMED STATE PER FIELD, never a pair of booleans.
     *
     * THE DEFECT THIS REPLACES: the modal opened with "Give your design
     * a title" already in red under an untouched field. Errors survived
     * a close and were still on screen at the next open, and a blur off
     * the auto-focused title fired the validator on a field nobody had
     * typed in. Telling someone they are wrong before they start is the
     * fastest way to lose them.
     *
     * A `touched` boolean cannot express the state that matters here,
     * which is "typing, and not yet asked to be correct". These four
     * can:
     *
     *   untouched  never typed in. NEUTRAL, always. No message, no
     *              aria-invalid, no red border, whatever its value
     *   editing    being typed in for the first time, not yet judged
     *   valid      asked and correct
     *   invalid    asked and not correct. The only state that paints
     *
     * A field is ASKED on blur once it is being edited, and on Continue
     * or Submit whatever it is doing. Once asked, it re-answers as the
     * customer types, so a corrected field clears at the keystroke that
     * fixes it rather than at the next submit. */
    var FIELD = {
      UNTOUCHED: 'untouched',
      EDITING: 'editing',
      VALID: 'valid',
      INVALID: 'invalid',
    };
    var fieldState = {};
    FIELDS.forEach(function (name) { fieldState[name] = FIELD.UNTOUCHED; });

    function setStep(n) {
      stepEls.forEach(function (el) {
        var stepNum = parseInt(el.getAttribute('data-step'), 10);
        el.hidden = stepNum !== n;
      });

      /* Position AND completion, written in one place so the visual
         marker and aria-current can never disagree. */
      stepMarkers.forEach(function (el) {
        var num = parseInt(el.getAttribute('data-step-marker'), 10);
        var state = 'upcoming';
        if (num === n) state = 'current';
        else if (num < n) state = 'done';
        el.setAttribute('data-state', state);
        if (state === 'current') el.setAttribute('aria-current', 'step');
        else el.removeAttribute('aria-current');
      });

      if (stepLabel) stepLabel.textContent = 'Step ' + n + ' of 2';

      /* ONE PRIMARY ACTION PER STEP. Step 1 offers Continue. Step 2
         offers Back, quieter, and Submit. Nothing here reorders the
         DOM: tab order has to keep matching reading order. */
      if (nextBtn) nextBtn.hidden = n !== 1;
      if (backBtn) backBtn.hidden = n !== 2;
      if (submitFormBtn) submitFormBtn.hidden = n !== 2;

      if (n === 2) renderReview();
    }

    /* The element a customer actually looks at and a screen reader
       actually sits on. For title and description that is the input
       itself; for category it is the radiogroup, not the hidden input
       carrying its value; for the image it is the dropzone, not the
       opacity-0 file input inside it. aria-invalid written to something
       invisible is announced to nobody. */
    function fieldControl(name) {
      if (name === 'category') return categoryGroup;
      if (name === 'file_type') return fileTypeGroup;
      if (name === 'image') return dropzone;
      return form.querySelector('[name="' + name + '"]');
    }

    function errorEl(name) {
      return form.querySelector('[data-error-for="' + name + '"]');
    }

    /* The message goes into a paragraph that is already in the served
       markup and already named by its field's aria-describedby. Nothing
       here creates an element at the moment it has something to say:
       a container inserted alongside its own text is not reliably
       announced, and this one has to be. */
    function setError(fieldName, message) {
      var errEl = errorEl(fieldName);
      if (errEl) errEl.textContent = message || '';
      /* The file type radios carry the invalid state as well as their
         group: a radio is what keyboard focus lands on, so that is where
         a screen reader has to hear it. */
      if (fieldName === 'file_type') markFileTypeInvalid(!!message);
      var control = fieldControl(fieldName);
      if (!control) return;
      if (message) control.setAttribute('aria-invalid', 'true');
      else control.removeAttribute('aria-invalid');
    }

    function validateTitle(showError) {
      var v = (titleInput.value || '').trim();
      if (v.length === 0) {
        if (showError) setError('title', 'Give your design a title.');
        return false;
      }
      if (v.length < 3) {
        if (showError) setError('title', 'Title must be at least 3 characters.');
        return false;
      }
      if (v.length > 200) {
        if (showError) setError('title', 'Title must be 200 characters or fewer.');
        return false;
      }
      setError('title', '');
      return true;
    }

    function validateCategory(showError) {
      var v = categoryHidden ? categoryHidden.value : '';
      if (!v) {
        if (showError) setError('category', 'Pick the category that fits best.');
        return false;
      }
      setError('category', '');
      return true;
    }

    function selectedFileTypeInput() {
      for (var i = 0; i < fileTypeInputs.length; i += 1) {
        if (fileTypeInputs[i].checked) return fileTypeInputs[i];
      }
      return null;
    }

    function selectedFileType() {
      var input = selectedFileTypeInput();
      return input ? input.value : '';
    }

    function syncFileTypeChips() {
      fileTypeInputs.forEach(function (input) {
        var chip = input.closest('.kc-choice');
        if (chip) chip.classList.toggle('kc-chip--active', input.checked);
      });
    }

    function markFileTypeInvalid(invalid) {
      fileTypeInputs.forEach(function (input) {
        if (invalid) input.setAttribute('aria-invalid', 'true');
        else input.removeAttribute('aria-invalid');
      });
    }

    function validateFileType(showError) {
      /* A section rendered without the field has nothing to validate. */
      if (!fileTypeGroup) return true;
      var v = selectedFileType();
      if (v !== 'stl' && v !== 'svg') {
        if (showError) setError('file_type', 'Choose a file type: STL or SVG.');
        return false;
      }
      setError('file_type', '');
      return true;
    }

    function validateImage(showError) {
      var files = fileInput && fileInput.files ? fileInput.files : null;
      if (!files || files.length === 0) {
        if (showError) setError('image', 'Attach a reference image so the community knows what you have in mind.');
        return false;
      }
      var f = files[0];
      /* These two branches write their message only when showError is
         set, so a "silent" check run to decide which step to return the
         customer to cannot itself put an error on screen. */
      if (allowedMime.indexOf(f.type) === -1) {
        if (showError) setError('image', '"' + f.name + '" is not a supported format. Use JPG, PNG, or WebP.');
        return false;
      }
      if (f.size > maxFileSize) {
        if (showError) setError('image', '"' + f.name + '" is larger than 4 MB. Compress it and try again.');
        return false;
      }
      setError('image', '');
      return true;
    }

    function validateDescription(showError) {
      var v = (descInput.value || '').trim();
      if (v.length === 0) {
        if (showError) setError('description', 'Tell us about the design in the brief.');
        return false;
      }
      if (v.length < 50) {
        if (showError) setError('description', 'The brief needs at least 50 characters. Describe materials, scale, and what makes it yours.');
        return false;
      }
      if (v.length > 2000) {
        if (showError) setError('description', 'The brief must be 2000 characters or fewer.');
        return false;
      }
      setError('description', '');
      return true;
    }

    var validators = {
      title: validateTitle,
      category: validateCategory,
      file_type: validateFileType,
      image: validateImage,
      description: validateDescription,
    };

    function validateField(name, showError) {
      var fn = validators[name];
      return fn ? fn(showError) : true;
    }

    function debounceValidate(fieldName, fn, ms) {
      if (debounceTimers[fieldName]) clearTimeout(debounceTimers[fieldName]);
      debounceTimers[fieldName] = setTimeout(fn, ms || 300);
    }

    function clearDebounce(fieldName) {
      if (!debounceTimers[fieldName]) return;
      clearTimeout(debounceTimers[fieldName]);
      debounceTimers[fieldName] = null;
    }

    /* ASK. The only call that is allowed to put an error on screen, and
       therefore the only place a field leaves the neutral states. */
    function askField(name) {
      clearDebounce(name);
      var ok = validateField(name, true);
      fieldState[name] = ok ? FIELD.VALID : FIELD.INVALID;
      return ok;
    }

    /* First keystroke moves a field out of untouched and no further: it
       is being written, not being judged. Once it HAS been asked, every
       keystroke re-answers, and a value that has become correct clears
       immediately rather than on a timer, because an error that lingers
       after the fix reads as a UI that has not noticed. A value that is
       still wrong restates on a short debounce so the message does not
       thrash mid-word. */
    function onFieldInput(name) {
      if (fieldState[name] === FIELD.UNTOUCHED) {
        fieldState[name] = FIELD.EDITING;
        return;
      }
      if (fieldState[name] === FIELD.EDITING) return;
      if (validateField(name, false)) {
        clearDebounce(name);
        fieldState[name] = FIELD.VALID;
        setError(name, '');
        return;
      }
      fieldState[name] = FIELD.INVALID;
      debounceValidate(name, function () { validateField(name, true); }, 350);
    }

    /* Blur asks, unless the field is still untouched. The modal puts
       focus in the title on open; tabbing straight out of it is not the
       customer telling us they are finished with it. */
    function onFieldBlur(name) {
      if (fieldState[name] === FIELD.UNTOUCHED) return;
      askField(name);
    }

    /* Re-render the error layer from the field states. On a fresh form
       every field is untouched, so this paints nothing, which is the
       whole point: an error can no longer survive a close and greet the
       next open. On a form reopened after a failed submit the invalid
       fields keep their messages, because those were earned. */
    function repaintErrors() {
      FIELDS.forEach(function (name) {
        if (fieldState[name] === FIELD.INVALID) validateField(name, true);
        else setError(name, '');
      });
    }

    if (titleInput) {
      titleInput.addEventListener('blur', function () { onFieldBlur('title'); });
      titleInput.addEventListener('input', function () { onFieldInput('title'); });
    }
    if (descInput) {
      descInput.addEventListener('blur', function () { onFieldBlur('description'); });
      descInput.addEventListener('input', function () {
        updateDescriptionCounter();
        onFieldInput('description');
      });
      updateDescriptionCounter();
    }

    function updateDescriptionCounter() {
      if (!descCounter || !descInput) return;
      var len = (descInput.value || '').length;
      descCounter.textContent = len + ' / 2000';
      var state = '';
      if (len > 2000) state = 'over';
      else if (len > 1800) state = 'warn';
      if (state) descCounter.setAttribute('data-state', state);
      else descCounter.removeAttribute('data-state');
    }

    /* role="radiogroup" is a promise about the keyboard, not a label.
       It shipped with neither arrow keys nor a roving tabindex, so the
       group was seven separate tab stops that announced themselves as
       radios and then did not behave like any. Either implement the
       pattern or drop to role="group" with aria-pressed; this
       implements it.

       selectCategory is the one place selection is written, so the
       class, aria-checked, the tabindex and the hidden input can never
       disagree with each other. */
    var catChips = [];
    function selectCategory(chip, moveFocus) {
      if (!chip) return;
      if (categoryHidden) categoryHidden.value = chip.getAttribute('data-category-option') || '';
      catChips.forEach(function (other) {
        var isActive = other === chip;
        other.classList.toggle('kc-chip--active', isActive);
        other.setAttribute('aria-checked', isActive ? 'true' : 'false');
        /* Exactly one tab stop in the group, and it is the selection. */
        other.setAttribute('tabindex', isActive ? '0' : '-1');
      });
      if (moveFocus && typeof chip.focus === 'function') chip.focus();
      /* Choosing IS the answer for a chip row: there is no half-typed
         state to protect, so the selection asks and passes in the same
         move and any standing error goes with it. */
      askField('category');
    }

    if (categoryGroup) {
      catChips = Array.prototype.slice.call(
        categoryGroup.querySelectorAll('[data-category-option]')
      );
      catChips.forEach(function (chip, index) {
        chip.addEventListener('click', function () {
          selectCategory(chip, false);
        });

        chip.addEventListener('keydown', function (e) {
          var key = e.key;
          var last = catChips.length - 1;
          var next = null;

          /* Both axes, because the chips wrap onto a second row at
             narrow widths and Up/Down is what a visitor reaches for
             once they do. Selection wraps, per the WAI-ARIA radio
             group pattern. */
          if (key === 'ArrowRight' || key === 'ArrowDown') {
            next = catChips[index === last ? 0 : index + 1];
          } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
            next = catChips[index === 0 ? last : index - 1];
          } else if (key === 'Home') {
            next = catChips[0];
          } else if (key === 'End') {
            next = catChips[last];
          } else if (key === ' ' || key === 'Spacebar') {
            next = chip;
          } else {
            return;
          }

          /* Stop the arrow from scrolling the dialog and Space from
             re-firing as a click on the button underneath. */
          e.preventDefault();
          selectCategory(next, true);
        });
      });
    }

    /* Choosing a file type is the answer, the same as a category chip:
       the change asks the field, which clears any standing error. The
       arrow keys move the native selection and fire change as well. */
    fileTypeInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        syncFileTypeChips();
        askField('file_type');
      });
    });
    /* A choice the browser restored (back/forward cache, form restore)
       is already checked before this script runs. */
    syncFileTypeChips();

    function formatBytes(n) {
      if (n < 1024) return n + ' B';
      if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
      return (n / (1024 * 1024)).toFixed(2) + ' MB';
    }

    /* The bytes the customer picked, read once and held so BOTH the
       dropzone thumbnail and the step 2 review can paint from them. The
       review has to show the actual image, not a filename standing in
       for one, or it is not a review. */
    var previewDataUrl = null;

    function showImagePreview(file) {
      if (!file) {
        previewDataUrl = null;
        dropzonePreview.hidden = true;
        dropzoneInner.hidden = false;
        if (dropzoneImg) dropzoneImg.removeAttribute('src');
        paintReviewImage();
        return;
      }
      dropzoneInner.hidden = true;
      dropzonePreview.hidden = false;
      if (dropzoneName) dropzoneName.textContent = file.name;
      if (dropzoneSize) dropzoneSize.textContent = formatBytes(file.size);
      if (window.FileReader) {
        var reader = new FileReader();
        reader.onload = function (e) {
          previewDataUrl = e.target.result;
          if (dropzoneImg) dropzoneImg.src = previewDataUrl;
          /* The read is async and the customer may already have pressed
             Continue, so the review repaints when the bytes land rather
             than only when the step opens. */
          paintReviewImage();
        };
        reader.readAsDataURL(file);
      }
    }

    if (fileInput) {
      fileInput.addEventListener('change', function () {
        var f = fileInput.files && fileInput.files[0];
        if (f) showImagePreview(f);
        askField('image');
      });
    }

    if (dropzone) {
      dropzone.addEventListener('dragover', function (e) {
        e.preventDefault();
        dropzone.setAttribute('data-drag', 'true');
      });
      dropzone.addEventListener('dragleave', function () {
        dropzone.removeAttribute('data-drag');
      });
      dropzone.addEventListener('drop', function (e) {
        e.preventDefault();
        dropzone.removeAttribute('data-drag');
        var files = e.dataTransfer && e.dataTransfer.files;
        if (files && files.length > 0 && fileInput) {
          var dt = new DataTransfer();
          dt.items.add(files[0]);
          fileInput.files = dt.files;
          showImagePreview(files[0]);
          askField('image');
        }
      });
      dropzone.addEventListener('keydown', function (e) {
        if ((e.key === 'Enter' || e.key === ' ') && fileInput) {
          e.preventDefault();
          fileInput.click();
        }
      });
    }

    if (dropzoneRemove) {
      dropzoneRemove.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (fileInput) fileInput.value = '';
        showImagePreview(null);
        /* Removing an image is a deliberate step towards picking a
           better one, not a mistake. The field goes back to neutral and
           Continue is what catches it if they never do. */
        fieldState.image = FIELD.UNTOUCHED;
        setError('image', '');
      });
    }

    function setStatus(message, tone) {
      if (!statusEl) return;
      if (!message) {
        statusEl.textContent = '';
        statusEl.hidden = true;
        statusEl.removeAttribute('data-tone');
        return;
      }
      statusEl.textContent = message;
      statusEl.hidden = false;
      if (tone) statusEl.setAttribute('data-tone', tone);
      /* This panel has no role="status" of its own: it ships `hidden`,
         and a hidden region is not observed, so its announcements would
         never fire. The permanent region does the announcing, and the
         panel carries no role in the Liquid so the same sentence cannot
         be spoken twice. */
      announce(message);
    }

    /* Walk the fields in the order they appear on the page rather than
       taking the first [aria-invalid] node, because the category group
       and the dropzone now carry that attribute and one of them is not
       focusable: the group is a div, so the chip inside it is what
       receives focus. Landing a customer on nothing is how a Continue
       press looks like a button that does not work. */
    function focusInvalidTarget(name) {
      if (name === 'category') return catChips[0] || categoryGroup;
      /* The group is a div and cannot take focus; the radio can. */
      if (name === 'file_type') return selectedFileTypeInput() || fileTypeInputs[0] || fileTypeGroup;
      if (name === 'image') return dropzone;
      return fieldControl(name);
    }

    function focusFirstInvalid() {
      for (var i = 0; i < FIELDS.length; i += 1) {
        var name = FIELDS[i];
        if (fieldState[name] !== FIELD.INVALID) continue;
        var target = focusInvalidTarget(name);
        if (target && typeof target.focus === 'function') {
          target.scrollIntoView({ block: 'center', behavior: 'smooth' });
          target.focus({ preventScroll: true });
          return true;
        }
        var errEl = errorEl(name);
        if (errEl) errEl.scrollIntoView({ block: 'center', behavior: 'smooth' });
        return false;
      }
      return false;
    }

    function askEveryField() {
      var ok = true;
      FIELDS.forEach(function (name) {
        if (!askField(name)) ok = false;
      });
      return ok;
    }

    /* THE REVIEW. Everything the post will consist of, drawn from the
       fields themselves so it cannot describe a submission other than
       the one about to be sent. textContent throughout: this is a
       customer's own typing rendered back to them and it is never
       markup. */
    function paintReviewImage() {
      if (!reviewImage) return;
      if (previewDataUrl) {
        reviewImage.src = previewDataUrl;
        reviewImage.hidden = false;
        if (reviewNoImage) reviewNoImage.hidden = true;
        return;
      }
      reviewImage.removeAttribute('src');
      reviewImage.hidden = true;
      if (reviewNoImage) reviewNoImage.hidden = false;
    }

    function renderReview() {
      if (reviewCategory) {
        reviewCategory.textContent = (categoryHidden && categoryHidden.value) || 'No category';
      }
      if (reviewTitle) {
        reviewTitle.textContent = ((titleInput && titleInput.value) || '').trim();
      }
      if (reviewByline) {
        /* Falls back to the same neutral label the server uses when a
           customer lookup fails, so the review never promises a byline
           the submission will not carry. */
        var name = ((bylineValue && bylineValue.textContent) || '').trim().replace(/^@/, '');
        reviewByline.textContent = 'Posted as @' + (name || 'Kinetic Carving member');
      }
      if (reviewFileType) {
        /* The chip's own words, so the review names the choice exactly as
           the customer saw it on step 1. */
        var ftInput = selectedFileTypeInput();
        var ftChip = ftInput ? ftInput.closest('.kc-choice') : null;
        var ftLabel = ftChip ? (ftChip.textContent || '').replace(/\s+/g, ' ').trim() : '';
        reviewFileType.textContent = 'File type: ' + (ftLabel || 'Not chosen');
      }
      if (reviewDescription) {
        reviewDescription.textContent = ((descInput && descInput.value) || '').trim();
      }
      paintReviewImage();
    }

    function goToReview() {
      if (!askEveryField()) {
        focusFirstInvalid();
        setStatus('Fix the highlighted fields to continue.', 'error');
        return;
      }
      setStatus('', '');
      setStep(2);
      /* Focus goes to the review itself, not to Submit: a keyboard
         customer who presses Continue and lands on the primary action
         is one Enter away from posting something they have not read
         yet, which is the opposite of what this step is for. */
      if (reviewStep && typeof reviewStep.focus === 'function') reviewStep.focus();
      announce('Step 2 of 2. Review your submission before posting.');
    }

    function onStepOne() {
      return !!reviewStep && reviewStep.hidden;
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', goToReview);
    }
    if (backBtn) {
      backBtn.addEventListener('click', function () {
        setStep(1);
        if (titleInput && typeof titleInput.focus === 'function') titleInput.focus();
        announce('Step 1 of 2. Details.');
      });
    }

    /* ONE NAMED STATE FOR THE WRITE, never an isSubmitting boolean.
     *
     *   idle       nothing sent
     *   in_flight  the POST is out. Submit is disabled, and so is Back:
     *              changing the answers under a request already
     *              carrying them helps nobody
     *   succeeded  the SERVER said it saved. Still disabled, because
     *              the dialog is mid-close
     *   failed     it did not save. Enabled again, message on screen,
     *              every answer still in the fields
     *
     * The old code disabled the button and re-enabled it in .finally(),
     * which runs the instant the response is read. On success that
     * re-enabled Submit for the whole 1400ms the confirmation was up,
     * so a second press posted the design twice. `succeeded` is the
     * state a boolean could not hold. */
    var SUBMIT = {
      IDLE: 'idle',
      IN_FLIGHT: 'in_flight',
      SUCCEEDED: 'succeeded',
      FAILED: 'failed',
    };
    var submitState = SUBMIT.IDLE;

    /* One key per submission, minted at the first press and REUSED by
       every retry of that same submission, so that once the server
       dedupes on it a request that times out and is sent again lands as
       one design on the board and not two. api/design-submit.js does
       not read the key yet; until it does, the 24-hour limit refuses
       the resend instead. A key minted inside the retry is the same as
       having no key. It is cleared when the form is reset for a new
       submission, and when the server refuses the content, because the
       next attempt is then a changed submission and not a retry. */
    var idempotencyKey = null;

    function setSubmitState(next) {
      submitState = next;
      form.setAttribute('data-submit-state', next);

      var busy = next === SUBMIT.IN_FLIGHT || next === SUBMIT.SUCCEEDED;
      if (submitFormBtn) {
        submitFormBtn.disabled = busy;
        if (busy) submitFormBtn.setAttribute('aria-disabled', 'true');
        else submitFormBtn.removeAttribute('aria-disabled');
      }
      if (backBtn) backBtn.disabled = busy;

      /* Swap only the text node, never textContent on the button.
         Setting textContent there wiped its child <svg> permanently, so
         the send icon never came back after the first attempt. */
      if (submitLabel) {
        if (next === SUBMIT.IN_FLIGHT) submitLabel.textContent = 'Sending';
        else if (next === SUBMIT.SUCCEEDED) submitLabel.textContent = 'Sent';
        else submitLabel.textContent = 'Submit to Board';
      }
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (submitState === SUBMIT.IN_FLIGHT || submitState === SUBMIT.SUCCEEDED) return;

      /* Enter in a text field submits the form to the first submit
         button, and `hidden` does not take a button out of that: on
         step 1 an Enter in the title field went straight to the POST
         path, past the review the customer had not seen. Enter on step
         1 means Continue, which is the only action step 1 offers. */
      if (onStepOne()) {
        goToReview();
        return;
      }

      if (!askEveryField()) {
        /* Every input lives on step 1, so anything invalid is back
           there. Sending the customer to the review to look at a
           problem they cannot edit would be worse than not moving. */
        setStep(1);
        focusFirstInvalid();
        setStatus('Fix the highlighted fields and try again.', 'error');
        return;
      }
      sendSubmission();
    });

    function sendSubmission() {
      var endpoint = apiBase ? apiBase + '/design-submit' : form.getAttribute('action');
      if (!endpoint) {
        setSubmitState(SUBMIT.FAILED);
        setStatus('Submissions are not configured on this page yet.', 'error');
        return;
      }

      if (!idempotencyKey) idempotencyKey = newIdempotencyKey();

      var fd = new FormData(form);
      fd.append('idempotency_key', idempotencyKey);

      setSubmitState(SUBMIT.IN_FLIGHT);
      setStatus('Uploading your design.', 'pending');

      fetch(endpoint, {
        method: 'POST',
        body: fd,
        credentials: 'same-origin',
        /* No Content-Type: the browser has to set the multipart
           boundary itself. Only the key rides along. */
        headers: { 'Idempotency-Key': idempotencyKey },
      })
        .then(function (resp) {
          return resp
            .json()
            .catch(function () { return null; })
            .then(function (json) { return { resp: resp, json: json }; });
        })
        .then(function (result) {
          /* TRANSFER COMPLETE IS NOT COMMIT. Success is read from the
             server's answer, never from the request having resolved:
             a 502 from the proxy resolves too, and so does an HTML
             error page. */
          if (result.resp.ok && result.json && result.json.ok) {
            setSubmitState(SUBMIT.SUCCEEDED);
            setStatus(
              (result.json && result.json.message) || 'Submission received. Thanks for the pitch.',
              'success'
            );
            window.setTimeout(function () {
              dialog.close();
              showToast('Submission received. David will review within 24 hours.');
              resetForm();
            }, 1400);
            return;
          }
          var failMessage = (result.json && result.json.error) || 'We could not save your submission. Please try again.';
          /* The key is kept only for an unchanged resend after a
             network or server failure. A refusal of what was sent (a
             4xx other than a timeout or the daily limit) means the next
             attempt carries different content, and a changed submission
             is not a retry, so it gets a new key. */
          var httpStatus = result.resp.status;
          /* 409 is kept too: it means the server is still saving this
             exact submission. Dropping the key there would turn the
             next press of Submit into a second, separate submission,
             which is the duplicate the key exists to prevent. */
          if (httpStatus >= 400 && httpStatus < 500 && httpStatus !== 408 && httpStatus !== 409 && httpStatus !== 429) {
            idempotencyKey = null;
          }
          /* The server names the field when the file type is what it
             refused, so its message lands next to the choice on step 1,
             where it can be changed, and the status panel points there
             rather than repeating the same sentence. */
          if (result.json && result.json.field === 'file_type' && fileTypeGroup) {
            setStep(1);
            idempotencyKey = null;
            fieldState.file_type = FIELD.INVALID;
            setError('file_type', failMessage);
            fail('Check the file type above and try again.');
            return;
          }
          fail(failMessage);
        })
        .catch(function (err) {
          console.error('[design-submit] network error', err);
          fail('Network error. Check your connection and try again.');
        });
    }

    /* A failed submit NEVER closes the dialog and never touches a
       field. The reason goes on screen, focus moves to it so it is
       reachable rather than merely present, and every word the customer
       typed is still there to send again. Losing their input on a
       failure is the defect, not the failure. */
    function fail(message) {
      setSubmitState(SUBMIT.FAILED);
      setStatus(message, 'error');
      if (statusEl && typeof statusEl.focus === 'function') {
        statusEl.focus({ preventScroll: true });
        statusEl.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }

    function resetForm() {
      form.reset();
      if (categoryHidden) categoryHidden.value = '';
      if (categoryGroup) {
        /* Back to the server-rendered state: nothing selected, and the
           first chip is the group's single tab stop again. Without the
           tabindex reset a reopened form has every chip at -1 and the
           group cannot be reached by keyboard at all. */
        catChips.forEach(function (chip, index) {
          chip.classList.remove('kc-chip--active');
          chip.setAttribute('aria-checked', 'false');
          chip.setAttribute('tabindex', index === 0 ? '0' : '-1');
        });
      }
      showImagePreview(null);
      /* form.reset() unchecks the radios without firing change, so the
         chips are brought back in line here. */
      syncFileTypeChips();
      /* Every field neutral again, and the errors cleared BY that fact
         rather than alongside it, so the two cannot drift. */
      FIELDS.forEach(function (name) {
        fieldState[name] = FIELD.UNTOUCHED;
        clearDebounce(name);
      });
      repaintErrors();
      setStatus('', '');
      idempotencyKey = null;
      setSubmitState(SUBMIT.IDLE);
      updateDescriptionCounter();
      setStep(1);
    }

    /* Third dialog, same helper. All three previously carried their
       own copy of open, close, focus restore and the Escape / Tab
       handler; this one shipped without a focus trap at all because of
       that duplication. Everything specific to this dialog is in the
       hooks below. */
    var dialog = createDialog(modal, {
      closeSelector: '[data-submit-close]',
      focusDelay: 40,
      initialFocus: function () { return titleInput; },
      onOpen: function () {
        setStep(1);
        setStatus('', '');
        /* NOTHING IS VALIDATED HERE. repaintErrors only re-states what
           the field states already earned, which on a form nobody has
           filled in yet is nothing at all. This is the line that keeps
           a red "Give your design a title" off an untouched field, and
           it is also what stops an error surviving a close and greeting
           the next open. */
        repaintErrors();
      },
    });

    setStep(1);
    setSubmitState(SUBMIT.IDLE);

    return { open: dialog.open, close: dialog.close };
  }

  /* ---------------------------------------------------------------
     The discussion thread.

     ONE NAMED STATE, on data-comments-state, for the same reason the
     dialog around it carries one: a pair of booleans cannot say "the
     last thread we loaded is on screen and we could not confirm it",
     which is the state this surface spends most of its life in.

       loading       nothing on screen yet
       empty         resolved, nobody has posted
       ready         on screen and confirmed
       stale         last known copy on screen, unconfirmed. Persistent
                     Retry, never a toast
       revalidating  stale copy on screen, re-check in flight
       error         nothing on screen and the read failed
       unavailable   switched off, or no proxy prefix configured

     ONE TRANSPORT. Everything here, reads included, goes through the
     App Proxy prefix on data-api-base. The design re-check still goes
     to the public read origin over CORS, because it is public and
     unsigned; a thread is not, because `is_you` and `can_comment` are
     answers about the person asking, and only the proxy can attest to
     who that is. A cross-origin read would render a signed-in customer
     the signed-out control.

     NOTHING CUSTOMER-AUTHORED IS EVER ASSIGNED THROUGH innerHTML. Every
     node is built with el(), which sets textContent. The endpoint also
     returns a pre-escaped body_html and it is deliberately ignored:
     the safe path must not depend on the server having escaped right.
     --------------------------------------------------------------- */

  var CSTATE = {
    UNAVAILABLE: 'unavailable',
    LOADING: 'loading',
    EMPTY: 'empty',
    READY: 'ready',
    STALE: 'stale',
    REVALIDATING: 'revalidating',
    ERROR: 'error',
  };

  var SVG_NS = 'http://www.w3.org/2000/svg';

  /* Drawn rather than typed. A glyph reaction control depends on the
     visitor's emoji font for its meaning and on their locale for its
     colour; a path is the same shape everywhere and inherits
     currentColor, which is what the pressed state changes. */
  function iconPath(dAttr) {
    var svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', 'kc-comment__reaction-glyph');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('width', '14');
    svg.setAttribute('height', '14');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    var path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', dAttr);
    svg.appendChild(path);
    return svg;
  }

  var REACTIONS = [
    {
      key: 'heart',
      label: 'Appreciate',
      d: 'M20.8 5.6a5.2 5.2 0 0 0-7.4 0L12 7l-1.4-1.4a5.2 5.2 0 1 0-7.4 7.4L12 21.6l8.8-8.6a5.2 5.2 0 0 0 0-7.4z',
    },
    {
      key: 'fire',
      label: 'Fire',
      d: 'M12 2.4c.4 3.6-2.2 4.6-3.4 6.6C7.4 11 6.6 12.6 6.6 14.4a5.4 5.4 0 0 0 10.8 0c0-2.8-1.8-4.4-2.8-6.4-.8 1.8-2.6 1.8-2.6-1.2z',
    },
  ];

  var COMMENT_MIN = 2;
  var COMMENT_MAX = 2000;

  /* How often the countdown beside an Edit control is repainted. The
     server's window is fifteen minutes, so half a minute is fine
     grained enough that the number on screen is never more than that
     wrong, and coarse enough to cost nothing. */
  var EDIT_SWEEP_MS = 30000;

  /* The wording of a deadline THE SERVER GAVE US. It is rendered, never
     decided: a comment with no edit_until gets no countdown rather than
     a made-up one. */
  function editWindowText(ms) {
    if (!(ms > 0)) return 'Edit window closed';
    var minutes = Math.ceil(ms / 60000);
    if (minutes <= 1) return 'Under a minute left to edit';
    return minutes + ' minutes left to edit';
  }

  function initComments(root, ctx) {
    var panel = root.querySelector('[data-detail-comments]');
    var inert = { openThread: function () {}, reset: function () {}, setWritable: function () {} };
    if (!panel) return inert;

    var listEl = panel.querySelector('[data-comments-list]');
    var countEl = panel.querySelector('[data-comments-count]');
    var emptyEl = panel.querySelector('[data-comments-empty]');
    var noteEl = panel.querySelector('[data-comments-note]');
    var footEl = panel.querySelector('.kc-comments__foot');
    var retryBtn = panel.querySelector('[data-comments-retry]');
    var formEl = panel.querySelector('[data-comments-form]');
    var inputEl = panel.querySelector('[data-comments-input]');
    var counterEl = panel.querySelector('[data-comments-counter]');
    var postBtn = panel.querySelector('[data-comments-post]');
    var errorEl = panel.querySelector('[data-comments-error]');
    var signinEl = panel.querySelector('[data-comments-signin]');
    var closedEl = panel.querySelector('[data-comments-closed]');
    var replyingEl = panel.querySelector('[data-comments-replying]');
    var replyingLabel = panel.querySelector('[data-comments-replying-label]');
    var replyCancel = panel.querySelector('[data-comments-reply-cancel]');

    var s = {
      handle: '',
      name: CSTATE.LOADING,
      items: [],
      count: 0,
      /* null is not false. It means we could not tell, which is what an
         unverified proxy request answers, and it falls back to the
         server-rendered signed-in flag rather than to "signed out". */
      canComment: null,
      /* Whether the DESIGN still accepts new comments, which is a
         different question from whether this VIEWER may write one. A
         withdrawn design refuses everybody; being signed out refuses
         only you. Three states again: null is "the re-check has not
         answered yet" and leaves the composer open, because a refusal
         from the endpoint keeps the customer's words on screen and a
         wrongly hidden box does not. */
      writable: null,
      token: 0,
      replyTo: null,
      posting: false,
    };

    var NOTE = {};
    NOTE[CSTATE.LOADING] = 'Loading the discussion...';
    NOTE[CSTATE.EMPTY] = '';
    NOTE[CSTATE.READY] = '';
    NOTE[CSTATE.STALE] = 'Showing the last thread we loaded. Not confirmed.';
    NOTE[CSTATE.REVALIDATING] = 'Re-checking the discussion...';
    NOTE[CSTATE.ERROR] = 'The discussion could not be loaded.';
    NOTE[CSTATE.UNAVAILABLE] = 'Comments are not switched on for this page.';

    /* The pending record. Written to storage BEFORE the request goes
       out, so a tab killed mid-post still knows there is an unposted
       comment and, more importantly, still holds the idempotency key
       that stops the retry writing a second one.

       Scoped to the viewer as well as the design. Without the viewer
       in the key, an unsent comment left by one account is offered to
       the next account on the same browser, with a button that posts
       it in their name. */
    function pendingKey(handle) {
      return 'kc-design-lab.pending-comment.' + ctx.viewerTag + '.' + handle;
    }

    function readPending() {
      if (!s.handle) return null;
      var record = store.get(pendingKey(s.handle));
      if (!record || typeof record.body !== 'string' || typeof record.key !== 'string') return null;
      return record;
    }

    function writePending(record) {
      if (s.handle) store.set(pendingKey(s.handle), record);
    }

    function clearPending() {
      if (s.handle) store.remove(pendingKey(s.handle));
    }

    function setState(name, message) {
      s.name = name;
      panel.setAttribute('data-comments-state', name);
      if (noteEl) noteEl.textContent = message || NOTE[name] || '';
      if (retryBtn) {
        retryBtn.hidden = !(name === CSTATE.STALE || name === CSTATE.ERROR);
      }
      if (emptyEl) emptyEl.hidden = name !== CSTATE.EMPTY;
      /* The row collapses when it has nothing in it. The panel is a
         flex column with a 16px gap, so a footer holding an empty
         note and a hidden button still costs its own height plus two
         gaps: measured at 52px of dead ground between the thread and
         the composer in the resting state. */
      if (footEl) {
        footEl.hidden = !(noteEl && noteEl.textContent) && !(retryBtn && !retryBtn.hidden);
      }
      paintComposerVisibility();
    }

    function paintComposerVisibility() {
      var offline = s.name === CSTATE.UNAVAILABLE;
      /* Falling back to the server-rendered flag is what keeps a
         signed-in customer from being shown the signed-out control
         while the viewer state is genuinely unknown. */
      var allowed = s.canComment === null ? ctx.isSignedIn : s.canComment === true;
      /* Closed beats both. A withdrawn design refuses a comment from a
         signed-in customer and a signed-out one alike, so neither the
         box nor the sign-in prompt is the right thing to show: offering
         sign-in for something sign-in cannot unlock is a dead end. */
      var closed = s.writable === false;
      if (formEl) formEl.hidden = offline || closed || !allowed;
      if (signinEl) signinEl.hidden = offline || closed || allowed;
      if (closedEl) closedEl.hidden = offline || !closed;
    }

    function paintCount() {
      if (!countEl) return;
      countEl.textContent = s.count > 0
        ? s.count + (s.count === 1 ? ' comment' : ' comments')
        : '';
    }

    function setComposerError(message) {
      if (errorEl) errorEl.textContent = message || '';
    }

    function updateCounter() {
      if (!counterEl || !inputEl) return;
      var len = (inputEl.value || '').length;
      counterEl.textContent = len + ' / ' + COMMENT_MAX;
      if (len > COMMENT_MAX) counterEl.setAttribute('data-state', 'over');
      else if (len > COMMENT_MAX - 200) counterEl.setAttribute('data-state', 'warn');
      else counterEl.removeAttribute('data-state');
    }

    /* ------------------------------------------------------------
       Rendering
       ------------------------------------------------------------ */

    function reactionControl(item, spec) {
      var block = item.reactions && item.reactions[spec.key];
      var n = block ? block.count || 0 : 0;
      var mine = block ? block.mine : null;

      /* mine === null means "not knowable from this request", which is
         NOT the same as false. Rendering a toggle from it would show a
         customer an un-pressed control over a reaction they already
         left, and their next click would silently remove it. Unknown
         viewers get the count as text. */
      if (mine === null) {
        var readOnly = el('span', 'kc-comment__reaction kc-comment__reaction--static');
        readOnly.appendChild(iconPath(spec.d));
        readOnly.appendChild(el('span', 'kc-comment__reaction-n kc-tabular', String(n)));
        readOnly.setAttribute('aria-label', spec.label + ', ' + n);
        return readOnly;
      }

      var btn = el('button', 'kc-comment__reaction');
      btn.type = 'button';
      btn.setAttribute('data-comment-react', spec.key);
      btn.setAttribute('aria-pressed', mine ? 'true' : 'false');
      btn.setAttribute('aria-label', spec.label + ', ' + n);
      btn.classList.toggle('kc-comment__reaction--mine', !!mine);
      btn.appendChild(iconPath(spec.d));
      btn.appendChild(el('span', 'kc-comment__reaction-n kc-tabular', String(n)));
      return btn;
    }

    function actionButton(attr, label, className) {
      var btn = el('button', 'kc-comment__action' + (className ? ' ' + className : ''), label);
      btn.type = 'button';
      btn.setAttribute(attr, '');
      return btn;
    }

    function commentNode(item, isReply) {
      var li = el('li', 'kc-comment' + (isReply ? ' kc-comment--reply' : ''));
      li.setAttribute('data-comment-id', item.id);

      var bubble = el('div', 'kc-comment__bubble');
      li.appendChild(bubble);

      var head = el('div', 'kc-comment__head');
      head.appendChild(el('span', 'kc-comment__author', item.author || 'Kinetic Carving member'));
      if (item.is_you) head.appendChild(el('span', 'kc-comment__you', 'You'));
      if (item.created_at) {
        var time = el('time', 'kc-comment__time kc-tabular', formatWhen(item.created_at));
        time.setAttribute('datetime', item.created_at);
        head.appendChild(time);
      }
      /* THE EDITED MARKER, with the time on it. "edited" on its own
         says a comment changed but not whether that happened before or
         after the replies under it, which is the only thing a reader of
         a thread actually wants to know. */
      if (item.edited_at) {
        var editedWhen = formatWhen(item.edited_at);
        var edited = el('time', 'kc-comment__edited', editedWhen ? 'edited ' + editedWhen : 'edited');
        edited.setAttribute('datetime', item.edited_at);
        head.appendChild(edited);
      }
      bubble.appendChild(head);

      /* Three ways a body can be absent, and each says which one it is.
         An empty bubble reads as a bug; "removed by a moderator" reads
         as moderation, which is what happened. */
      if (typeof item.body === 'string' && item.body) {
        bubble.appendChild(el('p', 'kc-comment__body', item.body));
      } else if (item.unavailable_reason === 'hidden_by_moderator') {
        bubble.appendChild(el('p', 'kc-comment__body kc-comment__body--withheld', 'Removed by a moderator.'));
      } else if (item.redacted) {
        bubble.appendChild(el('p', 'kc-comment__body kc-comment__body--withheld', 'This comment was removed by its author.'));
      } else {
        bubble.appendChild(el('p', 'kc-comment__body kc-comment__body--withheld', 'This comment could not be displayed.'));
      }

      var actions = el('div', 'kc-comment__actions');
      REACTIONS.forEach(function (spec) {
        actions.appendChild(reactionControl(item, spec));
      });

      /* A comment with nothing to act on offers nothing. Redacted means
         its author erased it; hidden means a moderator already took it
         down. Replying to either attributes a reply to nothing, and
         reporting a comment a moderator has already removed sends a
         moderator a report about their own decision. */
      var actionable = !item.redacted && !item.hidden;

      /* Replies go exactly one level deep, enforced by a check
         constraint in the schema. Offering Reply on a reply would be
         offering a control whose only outcome is a 400.

         A withdrawn design takes no new comments either, so Reply goes
         with the composer rather than staying behind as the one way
         back into a box that is not there. */
      if (!isReply && actionable && s.writable !== false) {
        actions.appendChild(actionButton('data-comment-reply', 'Reply'));
      }

      /* THE EDIT CONTROL.

         ELIGIBILITY IS THE SERVER'S ANSWER AND IS NOT RE-DERIVED HERE.
         `can_edit` arrives as true, false or null on every comment, and
         null is a third state: it means the endpoint could not tell who
         is asking. That is not permission and it is not refusal, so it
         draws no control at all. Nothing in this file inspects is_you
         and a clock to work the answer out for itself; the one place
         that rule lives is lib/design-comment-service.js, and the one
         place it is enforced is the WHERE clause of the update.

         `edit_until` is the deadline the server stated, rendered beside
         the button as a countdown. Honouring a deadline we were handed
         is not the same as inventing one: when it runs out the control
         is withdrawn locally, and the server would refuse anyway. */
      if (item.can_edit === true) {
        actions.appendChild(actionButton('data-comment-editown', 'Edit'));
        var until = Date.parse(item.edit_until);
        if (isFinite(until)) {
          var window_ = el('span', 'kc-comment__editwin', editWindowText(until - Date.now()));
          window_.setAttribute('data-comment-edit-until', String(until));
          actions.appendChild(window_);
        }
      }

      if (item.is_you && actionable) {
        actions.appendChild(actionButton('data-comment-delete', 'Remove', 'kc-comment__action--quiet'));
      } else if (!item.is_you && actionable) {
        actions.appendChild(actionButton('data-comment-report', 'Report', 'kc-comment__action--quiet'));
      }
      bubble.appendChild(actions);

      /* Inline confirm rather than a fourth dialog. The three dialogs
         on this board already share one open/close/focus-trap helper,
         and a destructive confirm does not need a modal to be clear. */
      var confirmBox = el('div', 'kc-comment__confirm');
      confirmBox.setAttribute('data-comment-confirm', '');
      confirmBox.hidden = true;
      bubble.appendChild(confirmBox);

      var status = el('p', 'kc-comment__status');
      status.setAttribute('data-comment-status', '');
      bubble.appendChild(status);

      if (!isReply) {
        var replies = el('ol', 'kc-comment__replies');
        replies.setAttribute('data-comment-replies', '');
        (item.replies || []).forEach(function (reply) {
          replies.appendChild(commentNode(reply, true));
        });
        li.appendChild(replies);
      }

      return li;
    }

    function renderList() {
      if (!listEl) return;
      listEl.textContent = '';
      s.items.forEach(function (item) {
        listEl.appendChild(commentNode(item, false));
      });
      paintCount();
      renderPending();
    }

    /* An unposted comment, shown as unposted. It is not styled as a
       comment in the thread, because it is not one: it never reached
       the server. Retry reuses the SAME idempotency key. */
    function renderPending() {
      if (!listEl) return;
      var existing = listEl.querySelector('[data-comment-unsent]');
      if (existing) existing.remove();

      var record = readPending();
      if (!record || s.posting) return;

      var li = el('li', 'kc-comment kc-comment--unsent');
      li.setAttribute('data-comment-unsent', '');
      var bubble = el('div', 'kc-comment__bubble');
      li.appendChild(bubble);
      bubble.appendChild(el('p', 'kc-comment__unsent-head', 'Not posted'));
      bubble.appendChild(el('p', 'kc-comment__body', record.body));
      if (record.message) bubble.appendChild(el('p', 'kc-comment__status', record.message));

      var actions = el('div', 'kc-comment__actions');
      actions.appendChild(actionButton('data-comment-resend', 'Try posting again'));
      /* Edit takes the words back to the composer and drops the record,
         key and all. That is correct rather than convenient: an edited
         body is a different comment, so reusing the old key would make
         the server replay the first attempt. */
      actions.appendChild(actionButton('data-comment-edit', 'Edit', 'kc-comment__action--quiet'));
      actions.appendChild(actionButton('data-comment-discard', 'Discard', 'kc-comment__action--quiet'));
      bubble.appendChild(actions);

      listEl.appendChild(li);
    }

    function findItem(id) {
      for (var i = 0; i < s.items.length; i += 1) {
        if (s.items[i].id === id) return { item: s.items[i], parent: null };
        var replies = s.items[i].replies || [];
        for (var j = 0; j < replies.length; j += 1) {
          if (replies[j].id === id) return { item: replies[j], parent: s.items[i] };
        }
      }
      return null;
    }

    /* Copy the server's row onto the one already in state, field by
       field, instead of swapping the object. `replies` is deliberately
       skipped: the edit endpoint answers with the comment, not with the
       thread under it, and taking its (absent) replies would erase a
       conversation because somebody fixed a typo. `reactions` is
       skipped for the same reason: the edit endpoint does not own the
       reaction counts and answers with zeros for every one. */
    function mergeItem(target, next) {
      if (!target || !next) return target;
      Object.keys(next).forEach(function (key) {
        if (key === 'replies' || key === 'reactions') return;
        target[key] = next[key];
      });
      return target;
    }

    /* ------------------------------------------------------------
       Editing your own comment.

       Inline, in the thread, not a modal. An edit is a correction to
       something the reader is looking at, and lifting it out of the
       conversation into a dialog loses the context that makes it
       obvious what is being corrected.
       ------------------------------------------------------------ */

    /* The bubble belonging to THIS comment, not the first bubble found
       anywhere under it. A top-level comment carries its replies as
       siblings of its own bubble, and a plain querySelector on the li
       would reach into them. */
    function bubbleOf(li) {
      var node = li.firstElementChild;
      while (node) {
        if (node.classList && node.classList.contains('kc-comment__bubble')) return node;
        node = node.nextElementSibling;
      }
      return null;
    }

    function closeEditor(li) {
      var bubble = bubbleOf(li);
      if (!bubble) return;
      var editor = bubble.querySelector('[data-comment-editor]');
      if (editor && editor.parentNode) editor.parentNode.removeChild(editor);
      var body = bubble.querySelector('.kc-comment__body');
      if (body) body.hidden = false;
      var actions = bubble.querySelector('.kc-comment__actions');
      if (actions) actions.hidden = false;
    }

    /*
     * THE IDEMPOTENCY KEY IS PER (COMMENT, TEXT), and it is held on the
     * row rather than on the editor, because the editor is destroyed and
     * rebuilt on a refusal and a key that dies with it is the same as no
     * key at all.
     *
     * Retrying the SAME words reuses the key, so an edit that committed
     * and then timed out replays the server's answer instead of writing
     * a second time. Changing the words mints a NEW key, because the
     * server treats one key with two different bodies as a conflict and
     * would refuse the second attempt outright.
     */
    function editKeyFor(li, text) {
      if (!li._kcEditKey || li._kcEditKeyText !== text) {
        li._kcEditKey = newIdempotencyKey();
        li._kcEditKeyText = text;
      }
      return li._kcEditKey;
    }

    /**
     * Open the inline editor on one comment.
     * @param {Element} li
     * @param {{text?: string, error?: string}} [prefill] the words a
     *        refused attempt left behind, and the reason it was refused.
     */
    function startEdit(li, prefill) {
      var id = li.getAttribute('data-comment-id');
      var found = findItem(id);
      if (!found || !found.item) return;
      var item = found.item;
      var bubble = bubbleOf(li);
      if (!bubble || bubble.querySelector('[data-comment-editor]')) return;

      var bodyEl = bubble.querySelector('.kc-comment__body');
      var actionsEl = bubble.querySelector('.kc-comment__actions');
      /* What the SERVER holds, which is what cancelling has to put
         back. Never the optimistic text. */
      var original = typeof item.body === 'string' ? item.body : '';
      var opening = prefill && typeof prefill.text === 'string' ? prefill.text : original;

      var editor = el('div', 'kc-comment__editor');
      editor.setAttribute('data-comment-editor', '');

      var input = document.createElement('textarea');
      input.className = 'kc-field__input kc-comment__editor-input';
      input.setAttribute('data-comment-editor-input', '');
      input.setAttribute('rows', '3');
      input.setAttribute('maxlength', String(COMMENT_MAX));
      input.setAttribute('aria-label', 'Edit your comment');
      input.value = opening;
      editor.appendChild(input);

      var foot = el('div', 'kc-comment__editor-foot');
      var counter = el('span', 'kc-comment__editor-counter kc-tabular', opening.length + ' / ' + COMMENT_MAX);
      foot.appendChild(counter);

      var acts = el('div', 'kc-comment__editor-actions');
      var saveBtn = el('button', 'kc-comment__action kc-comment__action--go', 'Save changes');
      saveBtn.type = 'button';
      var cancelBtn = el('button', 'kc-comment__action kc-comment__action--quiet', 'Cancel');
      cancelBtn.type = 'button';
      acts.appendChild(saveBtn);
      acts.appendChild(cancelBtn);
      foot.appendChild(acts);
      editor.appendChild(foot);

      var errEl = el('p', 'kc-comment__editor-error');
      errEl.setAttribute('data-comment-editor-error', '');
      if (prefill && prefill.error) errEl.textContent = prefill.error;
      editor.appendChild(errEl);

      if (bodyEl) bodyEl.hidden = true;
      if (actionsEl) actionsEl.hidden = true;
      bubble.insertBefore(editor, actionsEl || null);

      function cancelEdit() {
        closeEditor(li);
        /* Cancel restores the original text, and can do so without
           asking anybody, because nothing was sent. */
        if (bodyEl) bodyEl.textContent = original;
        var back = bubble.querySelector('[data-comment-editown]');
        if (back && typeof back.focus === 'function') back.focus();
        announce('Edit cancelled. Your comment is unchanged.');
      }

      function attemptSave() {
        var next = (input.value || '').trim();
        if (next.length < COMMENT_MIN) {
          errEl.textContent = 'Write something first. Two characters or more.';
          input.focus();
          return;
        }
        if (next.length > COMMENT_MAX) {
          errEl.textContent = 'Keep it under ' + COMMENT_MAX + ' characters.';
          input.focus();
          return;
        }
        if (next === original) {
          /* Identical text is not an edit, so it does not spend a round
             trip and does not stamp an "edited" marker on a comment
             nobody changed. */
          cancelEdit();
          return;
        }
        sendEdit(li, item, next, original, editKeyFor(li, next));
      }

      saveBtn.addEventListener('click', function (event) {
        event.preventDefault();
        attemptSave();
      });
      cancelBtn.addEventListener('click', function (event) {
        event.preventDefault();
        cancelEdit();
      });

      input.addEventListener('input', function () {
        counter.textContent = (input.value || '').length + ' / ' + COMMENT_MAX;
        if (errEl.textContent) errEl.textContent = '';
      });

      editor.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          /* The dialog's own Escape handler is bound to `document`, so
             this must not bubble. Without it one press cancels the edit
             AND closes the whole dialog, which is two actions from one
             key and loses the thread the customer was reading. */
          event.stopPropagation();
          cancelEdit();
          return;
        }
        if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
          event.preventDefault();
          attemptSave();
        }
      });

      if (typeof input.focus === 'function') {
        input.focus();
        try {
          input.setSelectionRange(input.value.length, input.value.length);
        } catch (err) {
          /* Selection is a nicety; a browser that refuses it still has a
             focused field with the right text in it. */
        }
      }
    }

    function sendEdit(li, item, next, original, key) {
      var id = item.id;
      var url = ctx.proxyUrl('/design-comment?comment=' + encodeURIComponent(id));
      if (!url) {
        var openEditor = bubbleOf(li) && bubbleOf(li).querySelector('[data-comment-editor-error]');
        if (openEditor) openEditor.textContent = 'Editing is not configured on this page yet.';
        return;
      }

      var bubble = bubbleOf(li);
      var bodyEl = bubble ? bubble.querySelector('.kc-comment__body') : null;

      /* OPTIMISTIC, AND MARKED AS OPTIMISTIC. The editor closes, the new
         words go up, and the bubble goes dashed and dimmed, which is a
         different thing on screen from a saved comment. There is
         therefore something visible to take back if this fails. */
      closeEditor(li);
      if (bodyEl) bodyEl.textContent = next;
      li.classList.add('kc-comment--pending');
      statusFor(li, 'Saving your edit...');

      /*
       * THE VISIBLE ROLLBACK.
       *
       * Two things have to happen together and neither is optional. The
       * original words go back into the bubble, because they are what
       * the server still holds and leaving the new ones there would
       * claim a save that did not happen. And the words the customer
       * typed go back into an OPEN editor with the reason underneath,
       * because losing what somebody typed on a failed save is a defect
       * whatever the reason for the failure was.
       */
      function rollback(message) {
        li.classList.remove('kc-comment--pending');
        if (bodyEl) bodyEl.textContent = original;
        statusFor(li, '');
        startEdit(li, { text: next, error: message });
        announce('That edit was not saved. ' + message);
      }

      fetch(url, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'Idempotency-Key': key,
        },
        body: JSON.stringify({ intent: 'edit', body: next, idempotency_key: key }),
      })
        .then(readJson)
        .then(function (result) {
          if (result.res.status === 401) {
            rollback('Sign in to edit.');
            ctx.promptLogin('comment');
            return;
          }
          if (!result.res.ok || !result.json.ok) {
            rollback(messageFor(result, 'That edit could not be saved.'));
            return;
          }

          /* Committed, not transferred. This is the row the server read
             back from the transaction that wrote it, carrying a fresh
             edited_at and a fresh can_edit, so the node is rebuilt from
             the answer rather than from what we sent. That is also how
             an edit the filter queued for review quietly loses its own
             Edit control: can_edit comes back false and the rebuilt
             node simply has no button. */
          li.classList.remove('kc-comment--pending');
          li._kcEditKey = null;
          li._kcEditKeyText = null;
          statusFor(li, '');

          var saved = result.json.comment;
          if (!saved || !li.parentNode) {
            announce('Your comment was updated.');
            return;
          }
          var found = findItem(id);
          if (!found) {
            announce('Your comment was updated.');
            return;
          }
          mergeItem(found.item, saved);
          var fresh = commentNode(found.item, !!found.parent);
          li.parentNode.replaceChild(fresh, li);
          /* Focus died with the node it was on. Putting it on the
             rebuilt bubble keeps a keyboard reader where they were
             instead of dropping them at the top of the dialog. */
          var freshBubble = bubbleOf(fresh);
          if (freshBubble) {
            freshBubble.setAttribute('tabindex', '-1');
            if (typeof freshBubble.focus === 'function') freshBubble.focus();
          }
          announce(result.json.unchanged ? 'No change to save.' : 'Your comment was updated.');
        })
        .catch(function (err) {
          console.error('[design-comment] edit failed', err);
          rollback('Network error. Your words are still here.');
        });
    }

    /* The countdown beside every open Edit control, repainted on one
       timer rather than one timer per comment. When a deadline the
       server gave us runs out, the control goes: leaving a button whose
       only outcome is a refusal is worse than taking it away, and an
       editor already open is deliberately left alone, because the
       server will answer that attempt on its own terms and the
       customer's words survive the refusal either way. */
    function sweepEditWindows() {
      if (!listEl || !listEl.firstElementChild) return;
      var nodes = listEl.querySelectorAll('[data-comment-edit-until]');
      Array.prototype.forEach.call(nodes, function (node) {
        var until = parseInt(node.getAttribute('data-comment-edit-until'), 10);
        if (!isFinite(until)) return;
        var left = until - Date.now();
        node.textContent = editWindowText(left);
        if (left > 0) return;
        node.removeAttribute('data-comment-edit-until');
        var row = node.closest('[data-comment-id]');
        var rowBubble = row ? bubbleOf(row) : null;
        if (!rowBubble || rowBubble.querySelector('[data-comment-editor]')) return;
        var btn = rowBubble.querySelector('[data-comment-editown]');
        if (btn && btn.parentNode) btn.parentNode.removeChild(btn);
      });
    }

    window.setInterval(sweepEditWindows, EDIT_SWEEP_MS);

    /* ------------------------------------------------------------
       Reading the thread
       ------------------------------------------------------------ */

    function load() {
      if (!s.handle) return;
      var url = ctx.proxyUrl('/design-comment?design=' + encodeURIComponent(s.handle));
      if (!url) {
        setState(CSTATE.UNAVAILABLE, 'Comments are not configured on this page yet.');
        return;
      }

      var token = ++s.token;
      setState(s.items.length ? CSTATE.REVALIDATING : CSTATE.LOADING);

      fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(readJson)
        .then(function (result) {
          if (token !== s.token) return;
          var payload = result.json;

          if (!result.res.ok || !payload.ok) {
            /* Stale is the honest answer whenever there is still a copy
               on screen. Wiping a readable thread because a re-check
               failed loses information the visitor already had. */
            setState(
              s.items.length ? CSTATE.STALE : CSTATE.ERROR,
              messageFor(result, 'The discussion could not be loaded.')
            );
            return;
          }

          var thread = payload.thread || {};
          s.items = thread.items || [];
          s.count = typeof thread.count === 'number' ? thread.count : s.items.length;
          s.canComment = payload.viewer ? payload.viewer.can_comment : null;
          renderList();

          /* The server's state 'stale' is not acted on here. It is
             computed from the age of the design mirror row
             (mirror_synced_at, older than six hours), which nothing
             refreshes after the row is created, while the comments in
             this answer are read live from the database. Showing that as
             a stale thread with a Retry button would be a false warning
             that Retry can never clear. Revisit this if the server ever
             uses 'stale' for the thread itself. */
          if (s.items.length === 0) {
            setState(CSTATE.EMPTY);
          } else if (thread.truncated) {
            setState(CSTATE.READY, 'Showing the first ' + thread.limit + ' comments.');
          } else {
            setState(CSTATE.READY);
          }
        })
        .catch(function (err) {
          if (token !== s.token) return;
          console.error('[design-comment] thread read failed', err);
          setState(
            s.items.length ? CSTATE.STALE : CSTATE.ERROR,
            'The discussion could not be loaded.'
          );
        });
    }

    /* ------------------------------------------------------------
       Posting
       ------------------------------------------------------------ */

    function setReplyTo(target) {
      s.replyTo = target;
      if (replyingEl) replyingEl.hidden = !target;
      if (replyingLabel && target) {
        replyingLabel.textContent = 'Replying to ' + target.author;
      }
      if (target && inputEl && typeof inputEl.focus === 'function') inputEl.focus();
    }

    function insertComment(comment, parentId) {
      if (!comment || !comment.id) return;
      if (parentId) {
        var found = findItem(parentId);
        if (found && found.item) {
          found.item.replies = found.item.replies || [];
          if (!found.item.replies.some(function (r) { return r.id === comment.id; })) {
            found.item.replies.push(comment);
            s.count += 1;
          }
        }
        return;
      }
      if (!s.items.some(function (r) { return r.id === comment.id; })) {
        comment.replies = comment.replies || [];
        s.items.push(comment);
        s.count += 1;
      }
    }

    function post(record) {
      var url = ctx.proxyUrl('/design-comment?design=' + encodeURIComponent(s.handle));
      if (!url) {
        setState(CSTATE.UNAVAILABLE, 'Comments are not configured on this page yet.');
        return;
      }

      s.posting = true;
      if (postBtn) {
        postBtn.disabled = true;
        postBtn.setAttribute('aria-disabled', 'true');
        postBtn.textContent = 'Posting...';
      }
      setComposerError('');
      renderPending();

      /* Optimistic, and marked as optimistic. The bubble is on screen
         immediately with data-comment-pending, which is a different
         thing on screen from a posted comment, so a failure has
         something visible to take back. */
      var optimistic = null;
      if (listEl) {
        optimistic = el('li', 'kc-comment kc-comment--pending');
        optimistic.setAttribute('data-comment-pending', '');
        var bubble = el('div', 'kc-comment__bubble');
        var head = el('div', 'kc-comment__head');
        head.appendChild(el('span', 'kc-comment__author', 'You'));
        head.appendChild(el('span', 'kc-comment__time', 'Posting...'));
        bubble.appendChild(head);
        bubble.appendChild(el('p', 'kc-comment__body', record.body));
        optimistic.appendChild(bubble);
        listEl.appendChild(optimistic);
      }

      function settle() {
        s.posting = false;
        if (postBtn) {
          postBtn.disabled = false;
          postBtn.removeAttribute('aria-disabled');
          postBtn.textContent = 'Post';
        }
      }

      /* THE VISIBLE ROLLBACK.

         The optimistic bubble comes OFF the thread, because it is not
         in the thread. What replaces it is an "Not posted" card holding
         the same words, which is a different thing on screen from a
         posted comment and cannot be mistaken for one. Nothing is
         silently dropped and nothing is silently kept.

         The words live in exactly one place, that card, so the visitor
         is never looking at two copies and wondering which one is real.
         The key is kept with them, so a retry after a timeout that
         actually committed replays the server's answer instead of
         writing a second comment. */
      function rollback(message) {
        if (optimistic && optimistic.parentNode) optimistic.remove();
        record.message = message;
        writePending(record);
        renderPending();
        setComposerError('Not posted. ' + message);
        announce('Your comment was not posted. ' + message);
      }

      fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'Idempotency-Key': record.key,
        },
        body: JSON.stringify({
          intent: 'comment',
          body: record.body,
          parent_id: record.parentId || null,
          idempotency_key: record.key,
        }),
      })
        .then(readJson)
        .then(function (result) {
          if (result.res.status === 401) {
            settle();
            rollback('Sign in to post.');
            ctx.promptLogin('comment');
            return;
          }
          if (!result.res.ok || !result.json.ok) {
            settle();
            rollback(messageFor(result, 'We could not post that. Please try again.'));
            return;
          }

          /* Committed, not transferred. This is the row the server read
             back from the transaction that wrote it, so the thread now
             shows what the database holds and not what we sent. */
          clearPending();
          settle();
          if (optimistic && optimistic.parentNode) optimistic.remove();
          insertComment(result.json.comment, record.parentId);
          renderList();
          setState(CSTATE.READY, result.json.replayed ? 'Already posted. Showing it now.' : '');
          setReplyTo(null);
          if (inputEl) {
            inputEl.value = '';
            updateCounter();
          }
          announce(record.parentId ? 'Reply posted.' : 'Comment posted.');
        })
        .catch(function (err) {
          console.error('[design-comment] post failed', err);
          settle();
          rollback('Network error. Your words are still here.');
        });
    }

    function submitFromComposer() {
      if (s.posting) return;
      var raw = inputEl ? (inputEl.value || '').trim() : '';
      if (raw.length < COMMENT_MIN) {
        setComposerError('Write something first. Two characters or more.');
        if (inputEl && typeof inputEl.focus === 'function') inputEl.focus();
        announce('Write something first.');
        return;
      }
      if (raw.length > COMMENT_MAX) {
        setComposerError('Keep it under ' + COMMENT_MAX + ' characters.');
        announce('That comment is too long.');
        return;
      }
      if (!ctx.isSignedIn && s.canComment !== true) {
        ctx.promptLogin('comment');
        return;
      }

      /* An existing unsent record for this design is REUSED, key and
         all, rather than replaced. That is what makes a retry one
         comment on the server instead of two. */
      var existing = readPending();
      var record = existing && existing.body === raw
        ? existing
        : { key: newIdempotencyKey(), body: raw, parentId: null, at: Date.now() };
      record.parentId = s.replyTo ? s.replyTo.id : record.parentId || null;
      record.message = '';

      /* Persisted BEFORE the request goes out. If the tab dies here,
         the reopened dialog still shows the words and still holds the
         key. */
      writePending(record);
      if (inputEl) {
        inputEl.value = '';
        updateCounter();
      }
      post(record);
    }

    /* ------------------------------------------------------------
       Per-comment actions
       ------------------------------------------------------------ */

    function statusFor(li, message) {
      var node = li.querySelector('[data-comment-status]');
      if (node) node.textContent = message || '';
    }

    function react(btn, li) {
      if (btn._kcInflight) return;
      var id = li.getAttribute('data-comment-id');
      var reaction = btn.getAttribute('data-comment-react');
      var url = ctx.proxyUrl(
        '/design-react?comment=' + encodeURIComponent(id) + '&reaction=' + encodeURIComponent(reaction)
      );
      if (!url) {
        statusFor(li, 'Reactions are not configured on this page yet.');
        return;
      }

      var wasOn = btn.getAttribute('aria-pressed') === 'true';
      var nEl = btn.querySelector('.kc-comment__reaction-n');
      var before = parseInt((nEl && nEl.textContent) || '0', 10) || 0;
      var next = Math.max(0, before + (wasOn ? -1 : 1));

      btn._kcInflight = true;
      btn.setAttribute('aria-pressed', wasOn ? 'false' : 'true');
      btn.classList.toggle('kc-comment__reaction--mine', !wasOn);
      if (nEl) nEl.textContent = String(next);

      function revert() {
        btn.setAttribute('aria-pressed', wasOn ? 'true' : 'false');
        btn.classList.toggle('kc-comment__reaction--mine', wasOn);
        if (nEl) nEl.textContent = String(before);
      }

      fetch(url, {
        method: wasOn ? 'DELETE' : 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'Idempotency-Key': newIdempotencyKey(),
        },
        body: JSON.stringify({ intent: wasOn ? 'off' : 'on' }),
      })
        .then(readJson)
        .then(function (result) {
          if (result.res.status === 401) {
            revert();
            ctx.promptLogin('comment');
            return;
          }
          if (!result.res.ok || !result.json.ok) {
            revert();
            statusFor(li, messageFor(result, 'That reaction did not save.'));
            announce('That reaction did not save.');
            return;
          }
          /* Snap to the server's count, never to the click. */
          var counts = result.json.counts || {};
          if (nEl) nEl.textContent = String(counts[reaction] || 0);
          var reacted = !!result.json.reacted;
          btn.setAttribute('aria-pressed', reacted ? 'true' : 'false');
          btn.classList.toggle('kc-comment__reaction--mine', reacted);
          statusFor(li, '');
        })
        .catch(function (err) {
          console.error('[design-react] failed', err);
          revert();
          statusFor(li, 'Network error. That reaction did not save.');
        })
        .then(function () {
          btn._kcInflight = false;
        });
    }

    function askConfirm(li, question, confirmLabel, run) {
      var box = li.querySelector('[data-comment-confirm]');
      if (!box) return;
      box.textContent = '';
      box.hidden = false;
      box.appendChild(el('span', 'kc-comment__confirm-copy', question));
      var yes = el('button', 'kc-comment__action kc-comment__action--danger', confirmLabel);
      yes.type = 'button';
      var no = el('button', 'kc-comment__action kc-comment__action--quiet', 'Keep it');
      no.type = 'button';
      yes.addEventListener('click', function () {
        box.hidden = true;
        box.textContent = '';
        run();
      });
      no.addEventListener('click', function () {
        box.hidden = true;
        box.textContent = '';
      });
      box.appendChild(yes);
      box.appendChild(no);
      if (typeof yes.focus === 'function') yes.focus();
    }

    function removeOwn(li) {
      var id = li.getAttribute('data-comment-id');
      var url = ctx.proxyUrl('/design-comment?comment=' + encodeURIComponent(id));
      if (!url) {
        statusFor(li, 'Comments are not configured on this page yet.');
        return;
      }
      statusFor(li, 'Removing...');

      /* One key per action, in the header and the body both, as post()
         does, so the request still carries it if a proxy drops the
         custom header. */
      var key = newIdempotencyKey();
      fetch(url, {
        method: 'DELETE',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'Idempotency-Key': key,
        },
        body: JSON.stringify({ idempotency_key: key }),
      })
        .then(readJson)
        .then(function (result) {
          if (result.res.status === 401) {
            statusFor(li, '');
            ctx.promptLogin('comment');
            return;
          }
          if (!result.res.ok || !result.json.ok) {
            statusFor(li, messageFor(result, 'That comment could not be removed.'));
            announce('That comment could not be removed.');
            return;
          }
          announce(result.json.message || 'Removed.');
          /* Re-read rather than splice. A removal is either a delete or
             a redaction in place depending on whether replies hang off
             it, and the server is the only thing that knows which. */
          load();
        })
        .catch(function (err) {
          console.error('[design-comment] delete failed', err);
          statusFor(li, 'Network error. That comment could not be removed.');
        });
    }

    function report(li) {
      var id = li.getAttribute('data-comment-id');
      var url = ctx.proxyUrl('/design-comment?intent=report');
      if (!url) {
        statusFor(li, 'Reporting is not configured on this page yet.');
        return;
      }
      statusFor(li, 'Sending the report...');

      var key = newIdempotencyKey();
      fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'Idempotency-Key': key,
        },
        body: JSON.stringify({ intent: 'report', comment_id: id, idempotency_key: key }),
      })
        .then(readJson)
        .then(function (result) {
          if (result.res.status === 401) {
            statusFor(li, '');
            ctx.promptLogin('comment');
            return;
          }
          if (!result.res.ok || !result.json.ok) {
            statusFor(li, messageFor(result, 'That report did not send.'));
            announce('That report did not send.');
            return;
          }
          var message = result.json.already_reported
            ? 'You have already reported this one.'
            : result.json.message || 'Reported. A moderator will look at it.';
          statusFor(li, message);
          announce(message);
          var btn = li.querySelector('[data-comment-report]');
          if (btn) {
            btn.disabled = true;
            btn.setAttribute('aria-disabled', 'true');
            btn.textContent = 'Reported';
          }
        })
        .catch(function (err) {
          console.error('[design-comment] report failed', err);
          statusFor(li, 'Network error. That report did not send.');
        });
    }

    /* ------------------------------------------------------------
       Wiring
       ------------------------------------------------------------ */

    if (listEl) {
      listEl.addEventListener('click', function (event) {
        var li = event.target.closest('[data-comment-id], [data-comment-unsent]');
        if (!li) return;

        if (event.target.closest('[data-comment-resend]')) {
          event.preventDefault();
          var record = readPending();
          if (record) post(record);
          return;
        }

        if (event.target.closest('[data-comment-edit]')) {
          event.preventDefault();
          var draft = readPending();
          clearPending();
          renderPending();
          setComposerError('');
          if (draft && inputEl) {
            inputEl.value = draft.body;
            updateCounter();
            if (typeof inputEl.focus === 'function') inputEl.focus();
          }
          announce('Moved back to the box for editing.');
          return;
        }

        if (event.target.closest('[data-comment-discard]')) {
          event.preventDefault();
          clearPending();
          renderPending();
          setComposerError('');
          announce('That comment was discarded.');
          return;
        }

        var reactBtn = event.target.closest('[data-comment-react]');
        if (reactBtn) {
          event.preventDefault();
          if (!ctx.isSignedIn && s.canComment !== true) {
            ctx.promptLogin('comment');
            return;
          }
          react(reactBtn, li);
          return;
        }

        if (event.target.closest('[data-comment-reply]')) {
          event.preventDefault();
          if (!ctx.isSignedIn && s.canComment !== true) {
            ctx.promptLogin('comment');
            return;
          }
          var authorEl = li.querySelector('.kc-comment__author');
          setReplyTo({
            id: li.getAttribute('data-comment-id'),
            author: authorEl ? authorEl.textContent : 'this comment',
          });
          return;
        }

        /* Distinct from [data-comment-edit] above, which takes an
           UNSENT draft back to the composer. Two controls called Edit
           doing two different things needed two attributes; sharing one
           would have sent a real edit down the draft path. */
        if (event.target.closest('[data-comment-editown]')) {
          event.preventDefault();
          startEdit(li);
          return;
        }

        if (event.target.closest('[data-comment-delete]')) {
          event.preventDefault();
          askConfirm(li, 'Remove your comment?', 'Remove', function () { removeOwn(li); });
          return;
        }

        if (event.target.closest('[data-comment-report]')) {
          event.preventDefault();
          if (!ctx.isSignedIn && s.canComment !== true) {
            ctx.promptLogin('comment');
            return;
          }
          askConfirm(li, 'Send this to a moderator?', 'Report', function () { report(li); });
        }
      });
    }

    if (formEl) {
      formEl.addEventListener('submit', function (event) {
        event.preventDefault();
        submitFromComposer();
      });
    }

    if (inputEl) {
      inputEl.addEventListener('input', function () {
        updateCounter();
        if (errorEl && errorEl.textContent) setComposerError('');
      });
    }

    if (replyCancel) {
      replyCancel.addEventListener('click', function () {
        setReplyTo(null);
      });
    }

    if (retryBtn) {
      retryBtn.addEventListener('click', function () {
        load();
      });
    }

    return {
      /* Whether the DESIGN is still open for writing, told to us by the
         detail re-check rather than worked out here. Called after
         openThread, because openThread resets this along with the rest
         of the thread state whenever the handle changes.

         Re-renders the list as well as the composer: Reply lives on
         every top-level bubble and has to go with the box. */
      setWritable: function (writable) {
        var next = writable === false ? false : writable === true ? true : null;
        if (next === s.writable) return;
        s.writable = next;
        renderList();
        paintComposerVisibility();
      },
      openThread: function (handle) {
        if (!ctx.enabled) {
          setState(CSTATE.UNAVAILABLE);
          return;
        }
        if (!handle || handle === s.handle) return;
        s.handle = handle;
        s.items = [];
        s.count = 0;
        s.canComment = null;
        s.writable = null;
        setReplyTo(null);
        setComposerError('');
        if (inputEl) {
          /* The box starts empty. An unposted comment from a previous
             visit is rendered as its own "Not posted" card by
             renderList, which is the single place those words live. */
          inputEl.value = '';
          updateCounter();
        }
        renderList();
        load();
      },
      reset: function () {
        /* Abandon any in-flight read so a late answer cannot repaint a
           thread nobody is looking at. The unsent record survives on
           purpose: it belongs to the design, not to the dialog. */
        s.token += 1;
        s.handle = '';
        s.items = [];
        s.count = 0;
        s.canComment = null;
        s.writable = null;
        s.posting = false;
        setReplyTo(null);
        setComposerError('');
        if (listEl) listEl.textContent = '';
        paintCount();
        setState(CSTATE.LOADING);
      },
    };
  }

  /* ---------------------------------------------------------------
     The profile surface, storefront half.

     ONE REQUEST, THREE PANELS. /design-profile answers submissions,
     saves and comments in a single body, each section carrying its own
     named state, its own keyset cursor and its own error. So this
     fetches once and then paginates per section, rather than running
     three endpoints against one page.

     STALE IS THE POINT OF THE CACHE. A failed section comes back with
     fallback: 'last_known', which is the server saying "render the copy
     you already have, labelled". So the last good rows for each section
     are kept in sessionStorage and re-rendered under a stale label with
     a persistent Retry. An empty state in place of a failed read is a
     lie, and it is the specific lie this design avoids.

     PAGINATION IS KEYSET, NOT OFFSET. next_cursor is opaque, bound to
     the viewer and to its own section, and it is passed straight back
     as <section>_cursor. Nothing here parses it: an offset that the
     client computes drops a row the moment somebody saves a design
     while the page is open.

     IDENTITY IS NEVER IN A URL THIS FILE BUILDS. The endpoint refuses a
     request carrying a customer id outright, and there is nothing here
     to give it one.
     --------------------------------------------------------------- */

  var PSTATE = {
    LOADING: 'loading',
    EMPTY: 'empty',
    READY: 'ready',
    STALE: 'stale',
    ERROR: 'error',
  };

  var PROFILE_SECTIONS = ['submissions', 'saves', 'comments'];
  var PROFILE_PAGE_SIZE = 20;

  function initProfile(root) {
    if (!root || root.dataset.kcInit === '1') return;
    root.dataset.kcInit = '1';

    var apiBase = (root.getAttribute('data-api-base') || '').replace(/\/+$/, '');
    var boardUrl = root.getAttribute('data-board-url') || '/pages/design-lab';
    var isSignedIn = root.getAttribute('data-signed-in') === 'true';

    function proxyUrl(path) {
      return apiBase ? apiBase + path : null;
    }

    /* Signed out is rendered by Liquid at first paint with no request
       made, so there is nothing for the controller to do. */
    if (!isSignedIn) return;

    var panels = {};
    PROFILE_SECTIONS.forEach(function (name) {
      var panelRoot = root.querySelector('[data-profile-panel="' + name + '"]');
      if (!panelRoot) return;
      panels[name] = {
        name: name,
        root: panelRoot,
        list: panelRoot.querySelector('[data-panel-list]'),
        count: panelRoot.querySelector('[data-panel-count]'),
        note: panelRoot.querySelector('[data-panel-note]'),
        foot: panelRoot.querySelector('.kc-profile-panel__foot'),
        retry: panelRoot.querySelector('[data-panel-retry]'),
        more: panelRoot.querySelector('[data-panel-more]'),
        items: [],
        cursor: null,
        hasMore: false,
        state: PSTATE.LOADING,
        loading: false,
      };
    });

    if (Object.keys(panels).length === 0) return;

    /* Scoped to the viewer. Without that, a shared browser shows one
       customer's submissions to the next one under a "stale" label,
       which is worse than showing nothing. sessionStorage, so it dies
       with the tab rather than living on as an archive. */
    var viewerTag = root.getAttribute('data-viewer-tag') || 'anon';

    function cacheKey(name) {
      return 'kc-design-lab.profile.' + viewerTag + '.' + name;
    }

    function readCache(name) {
      var record = sessionStore.get(cacheKey(name));
      if (!record || !Array.isArray(record.items) || !record.items.length) return null;
      if (typeof record.at !== 'number' || !isFinite(record.at)) return null;
      return record;
    }

    function writeCache(name, items) {
      sessionStore.set(cacheKey(name), { items: items, at: Date.now() });
    }

    function setPanelState(panel, state, message) {
      panel.state = state;
      panel.root.setAttribute('data-panel-state', state);
      if (panel.note) panel.note.textContent = message || '';
      if (panel.retry) panel.retry.hidden = !(state === PSTATE.STALE || state === PSTATE.ERROR);
      if (panel.more) panel.more.hidden = !(panel.hasMore && state !== PSTATE.ERROR);
      if (panel.count) {
        panel.count.textContent = panel.items.length
          ? String(panel.items.length) + (panel.hasMore ? '+' : '')
          : '';
      }
      /* Same collapse as the thread's footer: an empty row in a gapped
         flex column is not free, it is its own height plus two gaps. */
      if (panel.foot) {
        panel.foot.hidden =
          !(panel.note && panel.note.textContent) &&
          !(panel.retry && !panel.retry.hidden) &&
          !(panel.more && !panel.more.hidden);
      }
    }

    function designHref(item) {
      var key = item.design_slug || item.design_handle || '';
      if (!key) return boardUrl;
      return boardUrl + (boardUrl.indexOf('?') === -1 ? '?' : '&') + 'design=' + encodeURIComponent(key);
    }

    function statusPill(status) {
      var label = status || '';
      if (status === 'InProduction') label = 'In Production';
      if (status === 'Completed') label = 'Forged';
      var pill = el('span', 'kc-design-card__status kc-profile-item__status');
      if (status) pill.classList.add('kc-design-card__status--' + String(status).toLowerCase());
      var dot = el('span', 'kc-design-card__status-dot');
      dot.setAttribute('aria-hidden', 'true');
      pill.appendChild(dot);
      pill.appendChild(el('span', null, label));
      return pill;
    }

    function metaRow(parts) {
      var row = el('div', 'kc-profile-item__meta');
      parts.forEach(function (part) {
        if (!part) return;
        row.appendChild(typeof part === 'string' ? el('span', 'kc-profile-item__meta-bit kc-tabular', part) : part);
      });
      return row;
    }

    function designItemNode(item, extraMeta) {
      var li = el('li', 'kc-profile-item');
      var link = el('a', 'kc-profile-item__link', item.title || 'Untitled design');
      link.href = designHref(item);
      li.appendChild(link);

      var meta = [];
      if (item.status) meta.push(statusPill(item.status));
      if (item.category) meta.push(el('span', 'kc-profile-item__meta-bit', item.category));
      if (typeof item.vote_count === 'number' && item.threshold) {
        meta.push(item.vote_count + ' / ' + item.threshold + ' votes');
      }
      if (typeof item.comment_count === 'number' && item.comment_count > 0) {
        meta.push(item.comment_count + (item.comment_count === 1 ? ' comment' : ' comments'));
      }
      if (extraMeta) meta.push(extraMeta);
      li.appendChild(metaRow(meta));

      if (item.product && item.product.url) {
        var product = el('a', 'kc-design-card__product kc-profile-item__product');
        product.href = item.product.url;
        product.appendChild(el('span', 'kc-design-card__product-label', 'Buy this piece'));
        li.appendChild(product);
      }
      return li;
    }

    function commentItemNode(item) {
      var li = el('li', 'kc-profile-item kc-profile-item--comment');

      /* The profile endpoint returns the stored body as text, not as
         pre-escaped markup. It goes in through textContent, which is
         the only reason that is safe. Nothing on this page ever assigns
         a customer-authored string through innerHTML. */
      li.appendChild(el('p', 'kc-profile-item__quote', item.body || ''));

      var link = el('a', 'kc-profile-item__link kc-profile-item__link--quiet', 'on ' + (item.design_title || 'a design'));
      link.href = designHref(item);
      li.appendChild(link);

      var meta = [];
      if (item.is_reply) meta.push(el('span', 'kc-profile-item__meta-bit', 'reply'));
      if (item.created_at) meta.push(formatWhen(item.created_at));
      if (item.edited_at) meta.push('edited');
      /* Said out loud to the author. A comment that silently vanishes
         from your own profile is what generates the support email. */
      if (item.moderation_state === 'hidden') {
        meta.push(el('span', 'kc-profile-item__meta-bit kc-profile-item__meta-bit--flag', 'Hidden by a moderator'));
      }
      li.appendChild(metaRow(meta));
      return li;
    }

    function nodeFor(name, item) {
      if (name === 'comments') return commentItemNode(item);
      if (name === 'saves') {
        return designItemNode(item, item.saved_at ? 'saved ' + formatWhen(item.saved_at) : null);
      }
      return designItemNode(item, item.submitted_at ? 'pitched ' + formatWhen(item.submitted_at) : null);
    }

    function renderPanel(panel) {
      if (!panel.list) return;
      panel.list.textContent = '';
      panel.items.forEach(function (item) {
        panel.list.appendChild(nodeFor(panel.name, item));
      });
    }

    /* The last known copy, in preference order: what is ON SCREEN
       first, the tab's cache second. The rows already rendered are the
       truest "last known" there is, and preferring the cache over them
       would wipe a list the customer was reading because a Show more
       failed. */
    function lastKnown(panel) {
      if (panel.items && panel.items.length) return { items: panel.items, at: null };
      var cached = readCache(panel.name);
      return cached ? { items: cached.items, at: cached.at } : null;
    }

    function staleNote(fallback) {
      if (!fallback.at) return 'Showing the list already on screen. Not confirmed.';
      return 'Showing your last loaded list, from ' + formatWhen(new Date(fallback.at).toISOString()) + '. Not confirmed.';
    }

    /* One section's answer, turned into one panel's state. Every branch
       of the endpoint's four is handled here by name, including the one
       the client is responsible for rendering: a failed section with
       fallback 'last_known' is the last good rows, labelled stale. */
    function applySection(panel, section, append) {
      if (!section) {
        setPanelState(panel, PSTATE.ERROR, 'That list did not come back.');
        return;
      }

      if (section.state === 'failed') {
        /* The server's own instruction. fallback: 'last_known' means
           "render the copy you already have, labelled", which is the
           only honest answer here: an empty state in place of a failed
           read is a lie about the customer's own data. */
        var fallback = lastKnown(panel);
        if (section.fallback === 'last_known' && fallback) {
          panel.items = fallback.items;
          panel.hasMore = false;
          renderPanel(panel);
          setPanelState(panel, PSTATE.STALE, staleNote(fallback));
          return;
        }
        setPanelState(
          panel,
          PSTATE.ERROR,
          (section.error && section.error.message) || 'That list could not be loaded. Please retry.'
        );
        return;
      }

      var items = section.items || [];
      panel.items = append ? panel.items.concat(items) : items;
      var page = section.page || {};
      panel.hasMore = !!page.has_more;
      panel.cursor = page.next_cursor || null;
      renderPanel(panel);

      if (panel.items.length === 0) {
        setPanelState(panel, PSTATE.EMPTY);
      } else {
        setPanelState(panel, PSTATE.READY);
      }
      writeCache(panel.name, panel.items);
    }

    /* @param {boolean} [wipe] true when the rows must come OFF the
     *        screen rather than being kept as a stale copy. */
    function failEverything(message, wipe) {
      PROFILE_SECTIONS.forEach(function (name) {
        var panel = panels[name];
        if (!panel) return;

        if (wipe) {
          panel.items = [];
          panel.hasMore = false;
          panel.cursor = null;
          sessionStore.remove(cacheKey(name));
          renderPanel(panel);
          setPanelState(panel, PSTATE.ERROR, message);
          return;
        }

        var fallback = lastKnown(panel);
        if (fallback) {
          panel.items = fallback.items;
          panel.hasMore = false;
          renderPanel(panel);
          setPanelState(panel, PSTATE.STALE, message + ' ' + staleNote(fallback));
        } else {
          setPanelState(panel, PSTATE.ERROR, message);
        }
      });
      announce(message);
    }

    /* @param {string[]} sections which sections to ask for
       @param {boolean} append true when this is a Show more */
    function load(sections, append) {
      var wanted = sections.filter(function (name) { return !!panels[name]; });
      if (wanted.length === 0) return;

      var params = ['limit=' + PROFILE_PAGE_SIZE, 'include=' + wanted.join(',')];
      wanted.forEach(function (name) {
        var panel = panels[name];
        panel.loading = true;
        if (append && panel.cursor) {
          params.push(encodeURIComponent(name + '_cursor') + '=' + encodeURIComponent(panel.cursor));
        }
        if (!append) {
          panel.cursor = null;
          setPanelState(panel, panel.items.length ? panel.state : PSTATE.LOADING);
        }
      });

      var url = proxyUrl('/design-profile?' + params.join('&'));
      if (!url) {
        failEverything('Your Design Lab is not configured on this page yet.');
        return;
      }

      fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(readJson)
        .then(function (result) {
          wanted.forEach(function (name) { panels[name].loading = false; });
          var payload = result.json;

          if (result.res.status === 401) {
            /* A 401 is the ONE failure that must not fall back to the
               last known copy. It means this request could no longer
               be tied to a signed-in customer, which is what a sign-out
               in another tab looks like. Leaving somebody's
               submissions, saves and comments on screen after that, on
               a browser that is very often shared, would be showing one
               person's data to whoever is in front of it now. The rows
               come off and the cache goes with them. */
            failEverything(
              messageFor(result, 'Open this page from the store while signed in.'),
              true
            );
            return;
          }
          if (!result.res.ok || !payload.ok || !payload.sections) {
            failEverything(messageFor(result, 'Your Design Lab could not be loaded. Please retry.'));
            return;
          }

          wanted.forEach(function (name) {
            applySection(panels[name], payload.sections[name], append);
          });

          if (payload.state === 'partial') {
            announce('Part of your Design Lab could not be loaded. The rest is shown.');
          }
        })
        .catch(function (err) {
          console.error('[design-profile] read failed', err);
          wanted.forEach(function (name) { panels[name].loading = false; });
          failEverything('Your Design Lab could not be loaded. Please retry.');
        });
    }

    PROFILE_SECTIONS.forEach(function (name) {
      var panel = panels[name];
      if (!panel) return;
      if (panel.retry) {
        panel.retry.addEventListener('click', function () {
          load([name], false);
        });
      }
      if (panel.more) {
        panel.more.addEventListener('click', function () {
          if (panel.loading || !panel.cursor) return;
          load([name], true);
        });
      }
    });

    load(PROFILE_SECTIONS, false);
  }

  /* THE LIVE REGION IS RENDERED IN LIQUID, NOT BUILT HERE.

     A toast created with role="status" and its text in the same frame
     is never announced. Assistive tech only announces a live region it
     was ALREADY observing when the content changed, so a region born
     holding its message announces nothing.

     [data-board-live] ships empty in the server-rendered HTML, so it
     is observed from first paint. Writing into it is the announcement;
     the toast is now purely the visual channel and carries no ARIA at
     all, which also stops the same sentence being announced twice.

     The clear-then-set on a frame boundary matters for the repeat
     case: setting identical text is not a change, so a second failed
     vote with the same message would not be announced without it. */
  function announce(message) {
    if (!message) return;
    var region = document.querySelector('[data-board-live]');
    if (!region) return;
    region.textContent = '';

    var write = function () {
      /* Idempotent, so the two schedulers below cannot fight. Setting
         identical text is not a change and would not be announced
         twice in any case, but not touching the node at all is
         cheaper and clearer. */
      if (region.textContent !== message) region.textContent = message;
    };

    /* rAF rather than setTimeout(0) so the empty state is committed to
       the accessibility tree before the new text lands.

       THE TIMER IS NOT BELT AND BRACES. requestAnimationFrame does not
       fire at all while the document is not rendering, and a callback
       that never runs leaves this region CLEARED and never re-filled:
       the message is not late, it is gone. Measured in Chrome with
       document.visibilityState 'hidden', where every announcement on
       this board was dropped. The timer is what makes the announcement
       survive that, and the write is idempotent so the ordinary
       visible case still lands on the frame boundary. */
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(write);
      window.setTimeout(write, 50);
    } else {
      window.setTimeout(write, 0);
    }
  }

  var toastTimer = null;
  function showToast(message) {
    announce(message);

    var existing = document.querySelector('.kc-design-toast');
    if (existing) existing.remove();
    if (toastTimer) clearTimeout(toastTimer);

    var toast = document.createElement('div');
    toast.className = 'kc-design-toast';
    /* Deliberately inert to assistive tech. announce() above owns the
       spoken channel; duplicating it here would say everything twice
       and would still be announcing from a freshly created node. */
    toast.setAttribute('aria-hidden', 'true');
    toast.textContent = message;
    toast.style.cssText = [
      'position:fixed',
      'left:50%',
      'bottom:24px',
      'transform:translateX(-50%)',
      'padding:12px 18px',
      /* Kept in step with the substrate pass and the one-red ruling:
         --kc-forge, --kc-chalk, --kc-r-md, and the brand #e83433 in
         the glow rather than the retired #E83C2E. #f0f0f0 on #1a1a1a
         is 15.27:1. */
      'background:#1a1a1a',
      'color:#f0f0f0',
      'border:1px solid #B42A1E',
      'border-radius:6px',
      'font-family:Inter,sans-serif',
      'font-size:13px',
      'letter-spacing:0.02em',
      'box-shadow:0 20px 40px -10px rgba(0,0,0,0.6), 0 0 20px rgba(232,52,51,0.18)',
      'z-index:9999',
    ].join(';');
    document.body.appendChild(toast);
    toastTimer = setTimeout(function () {
      toast.remove();
    }, 3200);
  }

  ready(function () {
    document.querySelectorAll('[data-design-board]').forEach(init);
    /* The profile surface reuses this controller rather than shipping a
       second one. It carries .kc-design-board for the token block, so a
       separate hook is what keeps the board's grid, dialogs and vote
       wiring from being run against a page that has none of them. */
    document.querySelectorAll('[data-design-profile]').forEach(initProfile);
  });
})();
